import React, { useEffect, useMemo, useRef, useState } from "react";
import { Helmet } from "react-helmet";
import styles from "./styles/Xenorush.module.css";
import { IconLoading } from "./assets/icons";
import { PasswordStrengthIndicator } from "./components/signup/PasswordStrengthIndicator";
import DialogVerifyRegister from "./components/dialogs/DialogVerifyRegister";
import useAuthedApi from "./hooks/useAuthedApi";
import { extractErrorMessage } from "./utils";

import logoSeason3 from "./assets/xenorush/logo-season3.png";
import heroGroup from "./assets/xenorush/hero-group.png";
import charPurple from "./assets/xenorush/char-purple.png";
import charYellow from "./assets/xenorush/char-yellow.png";
import section1Deco01 from "./assets/xenorush/section1/deco-01.png";
import section1Deco02 from "./assets/xenorush/section1/deco-02.png";
import section1Deco03 from "./assets/xenorush/section1/deco-03.png";
import section1Deco04 from "./assets/xenorush/section1/deco-04.png";
import section1Deco05 from "./assets/xenorush/section1/deco-05.png";
import section1Deco06 from "./assets/xenorush/section1/deco-06.png";
import section1Deco07 from "./assets/xenorush/section1/deco-07.png";
import section1Deco08 from "./assets/xenorush/section1/deco-08.png";
import section1HeroBackground from "./assets/xenorush/section1/group-1.png";
import section1HeroGridAccent from "./assets/xenorush/section1/grid.png";
import section1HeroAmount from "./assets/xenorush/section1/rp15m.png";
import section1HeroLeft from "./assets/xenorush/section1/image-2.png";
import section1HeroRight from "./assets/xenorush/section1/image-3.png";
import section1HeroLeftMobile from "./assets/xenorush/section1/image-2-mobile.png";
import section1HeroRightMobile from "./assets/xenorush/section1/image-3-mobile.png";
import monitorFrameAsset from "./assets/xenorush/monitor.png";
import monitorScreen from "./assets/xenorush/change-this.png";
import promoGirlPink from "./assets/xenorush/promo-girl-pink.png";
import promoBoyLaying from "./assets/xenorush/promo-boy-laying.png";
import section2Background from "./assets/xenorush/section2/bg-galaxy.png";
import section2PrizeRp5m from "./assets/xenorush/section2/prize-rp5m.png";
import section2TokenGold from "./assets/xenorush/section2/token-gold.png";
import section2TokenSilver from "./assets/xenorush/section2/token-silver.png";
import section2CharactersTop from "./assets/xenorush/section2/characters-top.png";
import thankYouTitle from "./assets/xenorush/thank-you-title.png";
import alreadyRegisteredTitle from "./assets/xenorush/already-registered-title.png";
import coinOne from "./assets/xenorush/coin-1.png";
import coinTwo from "./assets/xenorush/coin-2.png";
import coinThree from "./assets/xenorush/coin-3.png";
import arrowRight from "./assets/xenorush/arrow-right.svg";

import facebookIcon from "./assets/icons/facebook.svg";
import xIcon from "./assets/icons/x.svg";
import instagramIcon from "./assets/icons/instagram.svg";
import tiktokIcon from "./assets/icons/tiktok.svg";
import telegramIcon from "./assets/icons/telegram.svg";

const XENORUSH_REFERRAL_CODE = "XENORUSHS3";
const ANNOUNCEMENT_DATE_LABEL = "25 April 2026";
const ANNOUNCEMENT_DATE = new Date("2026-04-25T14:00:00+07:00");
const XENORIZE_URL = "https://xenorize.com/dashboard";
const REGISTRATION_DEADLINE = new Date("2026-04-20T23:59:00+07:00");

const normalizeWhatsapp = (value) => {
  const digits = value.replace(/\D/g, "");

  if (!digits) return "";
  if (digits.startsWith("62")) return `+${digits}`;
  if (digits.startsWith("0")) return `+62${digits.slice(1)}`;
  if (value.trim().startsWith("+")) return `+${digits}`;
  return `+62${digits}`;
};

const isValidWhatsapp = (value) => /^\+62\d{8,13}$/.test(value);

const faqItems = [
  {
    question: "Apakah event Xenorush ini berbayar?",
    answer:
      "Tidak. Partisipasi dalam Xenorush sepenuhnya gratis dan tidak dipungut biaya apa pun.",
  },
  {
    question: "Siapa saja yang bisa mengikuti Xenorush?",
    answer:
      "Xenorush terbuka untuk semua pengunjung yang hadir di event Clash of Legends.",
  },
  {
    question: "Untuk apa data saya digunakan?",
    answer:
      "Data yang Anda berikan hanya akan digunakan untuk keperluan campaign Xenorush dan komunikasi terkait program ini.",
  },
];

const termsItems = [
  "1 akun hanya berlaku untuk 1 entry",
  "Data harus valid dan dapat dihubungi",
  "Pemenang akan dihubungi via Email / WhatsApp",
  "Keputusan Xenorize bersifat final",
  "Hati-hati penipuan yang mengatasnamakan Xenorize.",
];

const Countdown = ({ targetDate }) => {
  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
    completed: false,
  });

  useEffect(() => {
    const timer = setInterval(() => {
      const now = new Date().getTime();
      const distance = targetDate.getTime() - now;

      if (distance < 0) {
        clearInterval(timer);
        setTimeLeft((prev) => ({ ...prev, completed: true }));
        window.location.reload();
        return;
      }

      setTimeLeft({
        days: Math.floor(distance / (1000 * 60 * 60 * 24)),
        hours: Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
        minutes: Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60)),
        seconds: Math.floor((distance % (1000 * 60)) / 1000),
        completed: false,
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [targetDate]);

  const TimeUnit = ({ value, label }) => (
    <div className={styles.countdownUnit}>
      <div className={styles.countdownValue}>
        {value.toString().padStart(2, "0")}
      </div>
      <div className={styles.countdownLabel}>{label}</div>
    </div>
  );

  return (
    <div className={styles.countdownWrapper}>
      <TimeUnit value={timeLeft.days} label="HARI" />
      <TimeUnit value={timeLeft.hours} label="JAM" />
      <TimeUnit value={timeLeft.minutes} label="MENIT" />
      <TimeUnit value={timeLeft.seconds} label="DETIK" />
    </div>
  );
};

const PasswordToggleIcon = ({ hidden }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    {hidden ? (
      <>
        <path
          d="M3 3L21 21"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
        />
        <path
          d="M10.58 10.58C10.21 10.95 10 11.46 10 12C10 13.1 10.9 14 12 14C12.54 14 13.05 13.79 13.42 13.42"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M9.88 5.09C10.56 4.86 11.27 4.75 12 4.75C16.5 4.75 20.08 8.74 21 12C20.66 13.2 19.88 14.61 18.7 15.8M14.12 18.91C13.44 19.14 12.73 19.25 12 19.25C7.5 19.25 3.92 15.26 3 12C3.34 10.8 4.12 9.39 5.3 8.2"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </>
    ) : (
      <>
        <path
          d="M2.46 12C3.73 8.94 7.34 5.75 12 5.75C16.66 5.75 20.27 8.94 21.54 12C20.27 15.06 16.66 18.25 12 18.25C7.34 18.25 3.73 15.06 2.46 12Z"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2" />
      </>
    )}
  </svg>
);

const Footer = () => {
  return (
    <footer className={styles.footer}>
      <div className={styles.footerDesktopContainer}>
        <div className={styles.leftSection}>
          <img src="/images/Xenorize logo_Purple_D.png" alt="Xenorize Logo" />
          <p>2025 Copyright by Xenorize. All rights reserved.</p>
        </div>

        <div className={styles.rightSection}>
          <a href="/terms-of-use" target="_blank" rel="noopener noreferrer">
            Terms of Use
          </a>
          <a href="/privacy-policy" target="_blank" rel="noopener noreferrer">
            Privacy Policy
          </a>
          <a
            href="https://xenorize.freshdesk.com/support/tickets/new"
            target="_blank"
            rel="noopener noreferrer"
          >
            Contact Us
          </a>
        </div>

        <div>
          <div className={styles.iconContainer}>
            <a
              href="https://www.facebook.com/Xenorize"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Facebook"
            >
              <img src={facebookIcon} alt="Facebook" />
            </a>
            <a
              href="https://x.com/xenorize"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="X (Twitter)"
            >
              <img src={xIcon} alt="X (Twitter)" />
            </a>
            <a
              href="https://www.instagram.com/xenorize.global/ "
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Instagram"
            >
              <img src={instagramIcon} alt="Instagram" />
            </a>
            <a
              href="https://www.tiktok.com/@xenorize.global"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="TikTok"
            >
              <img src={tiktokIcon} alt="TikTok" />
            </a>
            <a
              href="https://t.me/XenorizeTech"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Telegram"
            >
              <img src={telegramIcon} alt="Telegram" />
            </a>
          </div>
        </div>
      </div>
      <div className={styles.footerMobileContainer}>
        <div className={styles.centerSection}>
          <a href="/terms-of-use" target="_blank" rel="noopener noreferrer">
            Terms of Use
          </a>
          <a href="/privacy-policy" target="_blank" rel="noopener noreferrer">
            Privacy Policy
          </a>
          <a
            href="https://xenorize.freshdesk.com/support/tickets/new"
            target="_blank"
            rel="noopener noreferrer"
          >
            Contact Us
          </a>
        </div>
        <div className={styles.rightSection}>
          <a
            href="https://www.facebook.com/Xenorize"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Facebook"
          >
            <img src={facebookIcon} alt="Facebook" />
          </a>
          <a
            href="https://x.com/xenorize"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="X (Twitter)"
          >
            <img src={xIcon} alt="X (Twitter)" />
          </a>
          <a
            href="https://www.instagram.com/xenorize.global/ "
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Instagram"
          >
            <img src={instagramIcon} alt="Instagram" />
          </a>
          <a
            href="https://www.tiktok.com/@xenorize.global"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="TikTok"
          >
            <img src={tiktokIcon} alt="TikTok" />
          </a>
          <a
            href="https://t.me/XenorizeTech"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Telegram"
          >
            <img src={telegramIcon} alt="Telegram" />
          </a>
        </div>
        <p>© 2025. All rights reserved.</p>
      </div>
    </footer>
  );
};

const CompletionState = ({
  title,
  subtitle,
  buttonLabel,
  variant = "generic",
  onToggleDefault,
}) => (
  <>
    <section className={styles.completionPage}>
      {variant === "winners" && (
        <>
          <div
            className={styles.heroGridPlane + " !top-[0]"}
            aria-hidden="true"
          />
          <div className={styles.heroBackdrop2} />
        </>
      )}

      {variant !== "winners" && (
        <img
          src={logoSeason3}
          alt="XenoRush Season 3"
          className={styles.completionLogo}
        />
      )}
      {variant === "thank-you" ? (
        <img
          src={thankYouTitle}
          alt="Thank You!"
          className={styles.thankYouTitleImage}
        />
      ) : variant === "registered" ? (
        <img
          src={alreadyRegisteredTitle}
          alt="Kamu sudah terdaftar di XenoRush"
          className={styles.thankYouTitleImage}
        />
      ) : (
        <h1 className={styles.completionTitle}>{title}</h1>
      )}
      {subtitle ? (
        <p className={styles.completionSubtitle}>{subtitle}</p>
      ) : null}

      {variant !== "winners" && (
        <div className={styles.completionArtworkWrap}>
          <img
            src={heroGroup}
            alt="XenoRush characters"
            className={styles.completionArtwork}
          />
        </div>
      )}

      {variant === "winners" ? (
        <div>
          {/* <img
          src={heroGroup}
          alt="Pemenang XenoRush Season 3"
          className={styles.completionArtwork}
        /> */}
          <div className={styles.heroDecorLayer} aria-hidden="true">
            <img
              src={section1Deco07}
              alt=""
              className={`${styles.heroTinyDeco} ${styles.heroTinyDecoOne}`}
            />
            <img
              src={section1Deco08}
              alt=""
              className={`${styles.heroTinyDeco} ${styles.heroTinyDecoTwo}`}
            />
            <img
              src={section1Deco04}
              alt=""
              className={`${styles.heroTinyDeco} ${styles.heroTinyDecoThree}`}
            />
            <img
              src={section1Deco02}
              alt=""
              className={`${styles.heroTinyDeco} ${styles.heroTinyDecoFour}`}
            />
            <img
              src={section1Deco05}
              alt=""
              className={`${styles.heroTinyDeco} ${styles.heroTinyDecoFive}`}
            />
            <img
              src={section1Deco06}
              alt=""
              className={`${styles.heroTinyDeco} ${styles.heroTinyDecoSix}`}
            />
            <img
              src={section1Deco08}
              alt=""
              className={`${styles.heroTinyDeco} ${styles.heroTinyDecoSeven}`}
            />
            <img
              src={section1Deco07}
              alt=""
              className={`${styles.heroTinyDeco} ${styles.heroTinyDecoEight}`}
            />
            <img
              src={section1HeroGridAccent}
              alt=""
              className={styles.heroCoinTop}
            />
          </div>

          <div className="flex flex-col items-center relative z-[1]">
            <div className={styles.heroEyebrow + " !mb-[4rem]"}>
              XenoRush Season 3 2026
            </div>

            <svg
              width="984"
              height="53"
              viewBox="0 0 984 53"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="w-full"
            >
              <path
                d="M4.01172 45.7031C2.75911 45.7031 1.77734 45.3815 1.06641 44.7383C0.355469 44.0612 0 43.2656 0 42.3516C0 41.4375 0.355469 40.6589 1.06641 40.0156C1.77734 39.3385 2.75911 39 4.01172 39H12.3398C13.5924 39 14.5742 39.3385 15.2852 40.0156C15.9961 40.6589 16.3516 41.4375 16.3516 42.3516C16.3516 43.2656 15.9961 44.0612 15.2852 44.7383C14.5742 45.3815 13.5924 45.7031 12.3398 45.7031H4.01172ZM4.01172 39.2031C2.75911 39.2031 1.77734 38.8815 1.06641 38.2383C0.355469 37.5612 0 36.7656 0 35.8516C0 34.9375 0.355469 34.1589 1.06641 33.5156C1.77734 32.8385 2.75911 32.5 4.01172 32.5H12.3398C13.5924 32.5 14.5742 32.8385 15.2852 33.5156C15.9961 34.1589 16.3516 34.9375 16.3516 35.8516C16.3516 36.7656 15.9961 37.5612 15.2852 38.2383C14.5742 38.8815 13.5924 39.2031 12.3398 39.2031H4.01172ZM4.01172 32.7031C2.75911 32.7031 1.77734 32.3815 1.06641 31.7383C0.355469 31.0612 0 30.2656 0 29.3516C0 28.4375 0.355469 27.6589 1.06641 27.0156C1.77734 26.3385 2.75911 26 4.01172 26H12.3398C13.5924 26 14.5742 26.3385 15.2852 27.0156C15.9961 27.6589 16.3516 28.4375 16.3516 29.3516C16.3516 30.2656 15.9961 31.0612 15.2852 31.7383C14.5742 32.3815 13.5924 32.7031 12.3398 32.7031H4.01172ZM4.01172 26.2031C2.75911 26.2031 1.77734 25.8815 1.06641 25.2383C0.355469 24.5612 0 23.7656 0 22.8516C0 21.9375 0.355469 21.1589 1.06641 20.5156C1.77734 19.8385 2.75911 19.5 4.01172 19.5H31.8398C33.0924 19.5 34.0742 19.8385 34.7852 20.5156C35.4961 21.1589 35.8516 21.9375 35.8516 22.8516C35.8516 23.7656 35.4961 24.5612 34.7852 25.2383C34.0742 25.8815 33.0924 26.2031 31.8398 26.2031H4.01172ZM4.01172 19.7031C2.75911 19.7031 1.77734 19.3815 1.06641 18.7383C0.355469 18.0612 0 17.2656 0 16.3516C0 15.4375 0.355469 14.6589 1.06641 14.0156C1.77734 13.3385 2.75911 13 4.01172 13H12.3398C13.5924 13 14.5742 13.3385 15.2852 14.0156C15.9961 14.6589 16.3516 15.4375 16.3516 16.3516C16.3516 17.2656 15.9961 18.0612 15.2852 18.7383C14.5742 19.3815 13.5924 19.7031 12.3398 19.7031H4.01172ZM30.0117 19.7031C28.7591 19.7031 27.7773 19.3815 27.0664 18.7383C26.3555 18.0612 26 17.2656 26 16.3516C26 15.4375 26.3555 14.6589 27.0664 14.0156C27.7773 13.3385 28.7591 13 30.0117 13H38.3398C39.5924 13 40.5742 13.3385 41.2852 14.0156C41.9961 14.6589 42.3516 15.4375 42.3516 16.3516C42.3516 17.2656 41.9961 18.0612 41.2852 18.7383C40.5742 19.3815 39.5924 19.7031 38.3398 19.7031H30.0117ZM4.01172 13.2031C2.75911 13.2031 1.77734 12.8815 1.06641 12.2383C0.355469 11.5612 0 10.7656 0 9.85156C0 8.9375 0.355469 8.15885 1.06641 7.51562C1.77734 6.83854 2.75911 6.5 4.01172 6.5H12.3398C13.5924 6.5 14.5742 6.83854 15.2852 7.51562C15.9961 8.15885 16.3516 8.9375 16.3516 9.85156C16.3516 10.7656 15.9961 11.5612 15.2852 12.2383C14.5742 12.8815 13.5924 13.2031 12.3398 13.2031H4.01172ZM30.0117 13.2031C28.7591 13.2031 27.7773 12.8815 27.0664 12.2383C26.3555 11.5612 26 10.7656 26 9.85156C26 8.9375 26.3555 8.15885 27.0664 7.51562C27.7773 6.83854 28.7591 6.5 30.0117 6.5H38.3398C39.5924 6.5 40.5742 6.83854 41.2852 7.51562C41.9961 8.15885 42.3516 8.9375 42.3516 9.85156C42.3516 10.7656 41.9961 11.5612 41.2852 12.2383C40.5742 12.8815 39.5924 13.2031 38.3398 13.2031H30.0117ZM4.01172 6.70312C2.75911 6.70312 1.77734 6.38151 1.06641 5.73828C0.355469 5.0612 0 4.26562 0 3.35156C0 2.4375 0.355469 1.65885 1.06641 1.01562C1.77734 0.338542 2.75911 0 4.01172 0H31.8398C33.0924 0 34.0742 0.338542 34.7852 1.01562C35.4961 1.65885 35.8516 2.4375 35.8516 3.35156C35.8516 4.26562 35.4961 5.0612 34.7852 5.73828C34.0742 6.38151 33.0924 6.70312 31.8398 6.70312H4.01172ZM62.7754 45.7031C61.5228 45.7031 60.541 45.3815 59.8301 44.7383C59.1191 44.0612 58.7637 43.2656 58.7637 42.3516C58.7637 41.4375 59.1191 40.6589 59.8301 40.0156C60.541 39.3385 61.5228 39 62.7754 39H84.1035C85.3561 39 86.3379 39.3385 87.0488 40.0156C87.7598 40.6589 88.1152 41.4375 88.1152 42.3516C88.1152 43.2656 87.7598 44.0612 87.0488 44.7383C86.3379 45.3815 85.3561 45.7031 84.1035 45.7031H62.7754ZM56.2754 39.2031C55.0228 39.2031 54.041 38.8815 53.3301 38.2383C52.6191 37.5612 52.2637 36.7656 52.2637 35.8516C52.2637 34.9375 52.6191 34.1589 53.3301 33.5156C54.041 32.8385 55.0228 32.5 56.2754 32.5H64.6035C65.8561 32.5 66.8379 32.8385 67.5488 33.5156C68.2598 34.1589 68.6152 34.9375 68.6152 35.8516C68.6152 36.7656 68.2598 37.5612 67.5488 38.2383C66.8379 38.8815 65.8561 39.2031 64.6035 39.2031H56.2754ZM56.2754 32.7031C55.0228 32.7031 54.041 32.3815 53.3301 31.7383C52.6191 31.0612 52.2637 30.2656 52.2637 29.3516C52.2637 28.4375 52.6191 27.6589 53.3301 27.0156C54.041 26.3385 55.0228 26 56.2754 26H90.6035C91.8561 26 92.8379 26.3385 93.5488 27.0156C94.2598 27.6589 94.6152 28.4375 94.6152 29.3516C94.6152 30.2656 94.2598 31.0612 93.5488 31.7383C92.8379 32.3815 91.8561 32.7031 90.6035 32.7031H56.2754ZM56.2754 26.2031C55.0228 26.2031 54.041 25.8815 53.3301 25.2383C52.6191 24.5612 52.2637 23.7656 52.2637 22.8516C52.2637 21.9375 52.6191 21.1589 53.3301 20.5156C54.041 19.8385 55.0228 19.5 56.2754 19.5H64.6035C65.8561 19.5 66.8379 19.8385 67.5488 20.5156C68.2598 21.1589 68.6152 21.9375 68.6152 22.8516C68.6152 23.7656 68.2598 24.5612 67.5488 25.2383C66.8379 25.8815 65.8561 26.2031 64.6035 26.2031H56.2754ZM82.2754 26.2031C81.0228 26.2031 80.041 25.8815 79.3301 25.2383C78.6191 24.5612 78.2637 23.7656 78.2637 22.8516C78.2637 21.9375 78.6191 21.1589 79.3301 20.5156C80.041 19.8385 81.0228 19.5 82.2754 19.5H90.6035C91.8561 19.5 92.8379 19.8385 93.5488 20.5156C94.2598 21.1589 94.6152 21.9375 94.6152 22.8516C94.6152 23.7656 94.2598 24.5612 93.5488 25.2383C92.8379 25.8815 91.8561 26.2031 90.6035 26.2031H82.2754ZM62.7754 19.7031C61.5228 19.7031 60.541 19.3815 59.8301 18.7383C59.1191 18.0612 58.7637 17.2656 58.7637 16.3516C58.7637 15.4375 59.1191 14.6589 59.8301 14.0156C60.541 13.3385 61.5228 13 62.7754 13H84.1035C85.3561 13 86.3379 13.3385 87.0488 14.0156C87.7598 14.6589 88.1152 15.4375 88.1152 16.3516C88.1152 17.2656 87.7598 18.0612 87.0488 18.7383C86.3379 19.3815 85.3561 19.7031 84.1035 19.7031H62.7754ZM108.539 45.7031C107.286 45.7031 106.305 45.3815 105.594 44.7383C104.883 44.0612 104.527 43.2656 104.527 42.3516C104.527 41.4375 104.883 40.6589 105.594 40.0156C106.305 39.3385 107.286 39 108.539 39H116.867C118.12 39 119.102 39.3385 119.812 40.0156C120.523 40.6589 120.879 41.4375 120.879 42.3516C120.879 43.2656 120.523 44.0612 119.812 44.7383C119.102 45.3815 118.12 45.7031 116.867 45.7031H108.539ZM134.539 45.7031C133.286 45.7031 132.305 45.3815 131.594 44.7383C130.883 44.0612 130.527 43.2656 130.527 42.3516C130.527 41.4375 130.883 40.6589 131.594 40.0156C132.305 39.3385 133.286 39 134.539 39H142.867C144.12 39 145.102 39.3385 145.812 40.0156C146.523 40.6589 146.879 41.4375 146.879 42.3516C146.879 43.2656 146.523 44.0612 145.812 44.7383C145.102 45.3815 144.12 45.7031 142.867 45.7031H134.539ZM108.539 39.2031C107.286 39.2031 106.305 38.8815 105.594 38.2383C104.883 37.5612 104.527 36.7656 104.527 35.8516C104.527 34.9375 104.883 34.1589 105.594 33.5156C106.305 32.8385 107.286 32.5 108.539 32.5H116.867C118.12 32.5 119.102 32.8385 119.812 33.5156C120.523 34.1589 120.879 34.9375 120.879 35.8516C120.879 36.7656 120.523 37.5612 119.812 38.2383C119.102 38.8815 118.12 39.2031 116.867 39.2031H108.539ZM134.539 39.2031C133.286 39.2031 132.305 38.8815 131.594 38.2383C130.883 37.5612 130.527 36.7656 130.527 35.8516C130.527 34.9375 130.883 34.1589 131.594 33.5156C132.305 32.8385 133.286 32.5 134.539 32.5H142.867C144.12 32.5 145.102 32.8385 145.812 33.5156C146.523 34.1589 146.879 34.9375 146.879 35.8516C146.879 36.7656 146.523 37.5612 145.812 38.2383C145.102 38.8815 144.12 39.2031 142.867 39.2031H134.539ZM108.539 32.7031C107.286 32.7031 106.305 32.3815 105.594 31.7383C104.883 31.0612 104.527 30.2656 104.527 29.3516C104.527 28.4375 104.883 27.6589 105.594 27.0156C106.305 26.3385 107.286 26 108.539 26H116.867C118.12 26 119.102 26.3385 119.812 27.0156C120.523 27.6589 120.879 28.4375 120.879 29.3516C120.879 30.2656 120.523 31.0612 119.812 31.7383C119.102 32.3815 118.12 32.7031 116.867 32.7031H108.539ZM134.539 32.7031C133.286 32.7031 132.305 32.3815 131.594 31.7383C130.883 31.0612 130.527 30.2656 130.527 29.3516C130.527 28.4375 130.883 27.6589 131.594 27.0156C132.305 26.3385 133.286 26 134.539 26H142.867C144.12 26 145.102 26.3385 145.812 27.0156C146.523 27.6589 146.879 28.4375 146.879 29.3516C146.879 30.2656 146.523 31.0612 145.812 31.7383C145.102 32.3815 144.12 32.7031 142.867 32.7031H134.539ZM108.539 26.2031C107.286 26.2031 106.305 25.8815 105.594 25.2383C104.883 24.5612 104.527 23.7656 104.527 22.8516C104.527 21.9375 104.883 21.1589 105.594 20.5156C106.305 19.8385 107.286 19.5 108.539 19.5H116.867C118.12 19.5 119.102 19.8385 119.812 20.5156C120.523 21.1589 120.879 21.9375 120.879 22.8516C120.879 23.7656 120.523 24.5612 119.812 25.2383C119.102 25.8815 118.12 26.2031 116.867 26.2031H108.539ZM134.539 26.2031C133.286 26.2031 132.305 25.8815 131.594 25.2383C130.883 24.5612 130.527 23.7656 130.527 22.8516C130.527 21.9375 130.883 21.1589 131.594 20.5156C132.305 19.8385 133.286 19.5 134.539 19.5H142.867C144.12 19.5 145.102 19.8385 145.812 20.5156C146.523 21.1589 146.879 21.9375 146.879 22.8516C146.879 23.7656 146.523 24.5612 145.812 25.2383C145.102 25.8815 144.12 26.2031 142.867 26.2031H134.539ZM108.539 19.7031C107.286 19.7031 106.305 19.3815 105.594 18.7383C104.883 18.0612 104.527 17.2656 104.527 16.3516C104.527 15.4375 104.883 14.6589 105.594 14.0156C106.305 13.3385 107.286 13 108.539 13H136.367C137.62 13 138.602 13.3385 139.312 14.0156C140.023 14.6589 140.379 15.4375 140.379 16.3516C140.379 17.2656 140.023 18.0612 139.312 18.7383C138.602 19.3815 137.62 19.7031 136.367 19.7031H108.539ZM160.803 52.2031C159.55 52.2031 158.568 51.8646 157.857 51.1875C157.146 50.5443 156.791 49.7656 156.791 48.8516C156.791 47.9375 157.146 47.1419 157.857 46.4648C158.568 45.8216 159.55 45.5 160.803 45.5H188.631C189.883 45.5 190.865 45.8216 191.576 46.4648C192.287 47.1419 192.643 47.9375 192.643 48.8516C192.643 49.7656 192.287 50.5443 191.576 51.1875C190.865 51.8646 189.883 52.2031 188.631 52.2031H160.803ZM186.803 45.7031C185.55 45.7031 184.568 45.3815 183.857 44.7383C183.146 44.0612 182.791 43.2656 182.791 42.3516C182.791 41.4375 183.146 40.6589 183.857 40.0156C184.568 39.3385 185.55 39 186.803 39H195.131C196.383 39 197.365 39.3385 198.076 40.0156C198.787 40.6589 199.143 41.4375 199.143 42.3516C199.143 43.2656 198.787 44.0612 198.076 44.7383C197.365 45.3815 196.383 45.7031 195.131 45.7031H186.803ZM167.303 39.2031C166.05 39.2031 165.068 38.8815 164.357 38.2383C163.646 37.5612 163.291 36.7656 163.291 35.8516C163.291 34.9375 163.646 34.1589 164.357 33.5156C165.068 32.8385 166.05 32.5 167.303 32.5H195.131C196.383 32.5 197.365 32.8385 198.076 33.5156C198.787 34.1589 199.143 34.9375 199.143 35.8516C199.143 36.7656 198.787 37.5612 198.076 38.2383C197.365 38.8815 196.383 39.2031 195.131 39.2031H167.303ZM160.803 32.7031C159.55 32.7031 158.568 32.3815 157.857 31.7383C157.146 31.0612 156.791 30.2656 156.791 29.3516C156.791 28.4375 157.146 27.6589 157.857 27.0156C158.568 26.3385 159.55 26 160.803 26H169.131C170.383 26 171.365 26.3385 172.076 27.0156C172.787 27.6589 173.143 28.4375 173.143 29.3516C173.143 30.2656 172.787 31.0612 172.076 31.7383C171.365 32.3815 170.383 32.7031 169.131 32.7031H160.803ZM186.803 32.7031C185.55 32.7031 184.568 32.3815 183.857 31.7383C183.146 31.0612 182.791 30.2656 182.791 29.3516C182.791 28.4375 183.146 27.6589 183.857 27.0156C184.568 26.3385 185.55 26 186.803 26H195.131C196.383 26 197.365 26.3385 198.076 27.0156C198.787 27.6589 199.143 28.4375 199.143 29.3516C199.143 30.2656 198.787 31.0612 198.076 31.7383C197.365 32.3815 196.383 32.7031 195.131 32.7031H186.803ZM160.803 26.2031C159.55 26.2031 158.568 25.8815 157.857 25.2383C157.146 24.5612 156.791 23.7656 156.791 22.8516C156.791 21.9375 157.146 21.1589 157.857 20.5156C158.568 19.8385 159.55 19.5 160.803 19.5H169.131C170.383 19.5 171.365 19.8385 172.076 20.5156C172.787 21.1589 173.143 21.9375 173.143 22.8516C173.143 23.7656 172.787 24.5612 172.076 25.2383C171.365 25.8815 170.383 26.2031 169.131 26.2031H160.803ZM186.803 26.2031C185.55 26.2031 184.568 25.8815 183.857 25.2383C183.146 24.5612 182.791 23.7656 182.791 22.8516C182.791 21.9375 183.146 21.1589 183.857 20.5156C184.568 19.8385 185.55 19.5 186.803 19.5H195.131C196.383 19.5 197.365 19.8385 198.076 20.5156C198.787 21.1589 199.143 21.9375 199.143 22.8516C199.143 23.7656 198.787 24.5612 198.076 25.2383C197.365 25.8815 196.383 26.2031 195.131 26.2031H186.803ZM167.303 19.7031C166.05 19.7031 165.068 19.3815 164.357 18.7383C163.646 18.0612 163.291 17.2656 163.291 16.3516C163.291 15.4375 163.646 14.6589 164.357 14.0156C165.068 13.3385 166.05 13 167.303 13H195.131C196.383 13 197.365 13.3385 198.076 14.0156C198.787 14.6589 199.143 15.4375 199.143 16.3516C199.143 17.2656 198.787 18.0612 198.076 18.7383C197.365 19.3815 196.383 19.7031 195.131 19.7031H167.303ZM219.566 45.7031C218.314 45.7031 217.332 45.3815 216.621 44.7383C215.91 44.0612 215.555 43.2656 215.555 42.3516C215.555 41.4375 215.91 40.6589 216.621 40.0156C217.332 39.3385 218.314 39 219.566 39H247.395C248.647 39 249.629 39.3385 250.34 40.0156C251.051 40.6589 251.406 41.4375 251.406 42.3516C251.406 43.2656 251.051 44.0612 250.34 44.7383C249.629 45.3815 248.647 45.7031 247.395 45.7031H219.566ZM213.066 39.2031C211.814 39.2031 210.832 38.8815 210.121 38.2383C209.41 37.5612 209.055 36.7656 209.055 35.8516C209.055 34.9375 209.41 34.1589 210.121 33.5156C210.832 32.8385 211.814 32.5 213.066 32.5H221.395C222.647 32.5 223.629 32.8385 224.34 33.5156C225.051 34.1589 225.406 34.9375 225.406 35.8516C225.406 36.7656 225.051 37.5612 224.34 38.2383C223.629 38.8815 222.647 39.2031 221.395 39.2031H213.066ZM239.066 39.2031C237.814 39.2031 236.832 38.8815 236.121 38.2383C235.41 37.5612 235.055 36.7656 235.055 35.8516C235.055 34.9375 235.41 34.1589 236.121 33.5156C236.832 32.8385 237.814 32.5 239.066 32.5H247.395C248.647 32.5 249.629 32.8385 250.34 33.5156C251.051 34.1589 251.406 34.9375 251.406 35.8516C251.406 36.7656 251.051 37.5612 250.34 38.2383C249.629 38.8815 248.647 39.2031 247.395 39.2031H239.066ZM213.066 32.7031C211.814 32.7031 210.832 32.3815 210.121 31.7383C209.41 31.0612 209.055 30.2656 209.055 29.3516C209.055 28.4375 209.41 27.6589 210.121 27.0156C210.832 26.3385 211.814 26 213.066 26H221.395C222.647 26 223.629 26.3385 224.34 27.0156C225.051 27.6589 225.406 28.4375 225.406 29.3516C225.406 30.2656 225.051 31.0612 224.34 31.7383C223.629 32.3815 222.647 32.7031 221.395 32.7031H213.066ZM239.066 32.7031C237.814 32.7031 236.832 32.3815 236.121 31.7383C235.41 31.0612 235.055 30.2656 235.055 29.3516C235.055 28.4375 235.41 27.6589 236.121 27.0156C236.832 26.3385 237.814 26 239.066 26H247.395C248.647 26 249.629 26.3385 250.34 27.0156C251.051 27.6589 251.406 28.4375 251.406 29.3516C251.406 30.2656 251.051 31.0612 250.34 31.7383C249.629 32.3815 248.647 32.7031 247.395 32.7031H239.066ZM213.066 26.2031C211.814 26.2031 210.832 25.8815 210.121 25.2383C209.41 24.5612 209.055 23.7656 209.055 22.8516C209.055 21.9375 209.41 21.1589 210.121 20.5156C210.832 19.8385 211.814 19.5 213.066 19.5H221.395C222.647 19.5 223.629 19.8385 224.34 20.5156C225.051 21.1589 225.406 21.9375 225.406 22.8516C225.406 23.7656 225.051 24.5612 224.34 25.2383C223.629 25.8815 222.647 26.2031 221.395 26.2031H213.066ZM239.066 26.2031C237.814 26.2031 236.832 25.8815 236.121 25.2383C235.41 24.5612 235.055 23.7656 235.055 22.8516C235.055 21.9375 235.41 21.1589 236.121 20.5156C236.832 19.8385 237.814 19.5 239.066 19.5H247.395C248.647 19.5 249.629 19.8385 250.34 20.5156C251.051 21.1589 251.406 21.9375 251.406 22.8516C251.406 23.7656 251.051 24.5612 250.34 25.2383C249.629 25.8815 248.647 26.2031 247.395 26.2031H239.066ZM213.066 19.7031C211.814 19.7031 210.832 19.3815 210.121 18.7383C209.41 18.0612 209.055 17.2656 209.055 16.3516C209.055 15.4375 209.41 14.6589 210.121 14.0156C210.832 13.3385 211.814 13 213.066 13H221.395C222.647 13 223.629 13.3385 224.34 14.0156C225.051 14.6589 225.406 15.4375 225.406 16.3516C225.406 17.2656 225.051 18.0612 224.34 18.7383C223.629 19.3815 222.647 19.7031 221.395 19.7031H213.066ZM239.066 19.7031C237.814 19.7031 236.832 19.3815 236.121 18.7383C235.41 18.0612 235.055 17.2656 235.055 16.3516C235.055 15.4375 235.41 14.6589 236.121 14.0156C236.832 13.3385 237.814 13 239.066 13H247.395C248.647 13 249.629 13.3385 250.34 14.0156C251.051 14.6589 251.406 15.4375 251.406 16.3516C251.406 17.2656 251.051 18.0612 250.34 18.7383C249.629 19.3815 248.647 19.7031 247.395 19.7031H239.066ZM265.33 45.7031C264.077 45.7031 263.096 45.3815 262.385 44.7383C261.674 44.0612 261.318 43.2656 261.318 42.3516C261.318 41.4375 261.674 40.6589 262.385 40.0156C263.096 39.3385 264.077 39 265.33 39H273.658C274.911 39 275.893 39.3385 276.604 40.0156C277.314 40.6589 277.67 41.4375 277.67 42.3516C277.67 43.2656 277.314 44.0612 276.604 44.7383C275.893 45.3815 274.911 45.7031 273.658 45.7031H265.33ZM297.83 45.7031C296.577 45.7031 295.596 45.3815 294.885 44.7383C294.174 44.0612 293.818 43.2656 293.818 42.3516C293.818 41.4375 294.174 40.6589 294.885 40.0156C295.596 39.3385 296.577 39 297.83 39H306.158C307.411 39 308.393 39.3385 309.104 40.0156C309.814 40.6589 310.17 41.4375 310.17 42.3516C310.17 43.2656 309.814 44.0612 309.104 44.7383C308.393 45.3815 307.411 45.7031 306.158 45.7031H297.83ZM265.33 39.2031C264.077 39.2031 263.096 38.8815 262.385 38.2383C261.674 37.5612 261.318 36.7656 261.318 35.8516C261.318 34.9375 261.674 34.1589 262.385 33.5156C263.096 32.8385 264.077 32.5 265.33 32.5H273.658C274.911 32.5 275.893 32.8385 276.604 33.5156C277.314 34.1589 277.67 34.9375 277.67 35.8516C277.67 36.7656 277.314 37.5612 276.604 38.2383C275.893 38.8815 274.911 39.2031 273.658 39.2031H265.33ZM284.221 39.2031C283.239 39.2031 282.426 38.8815 281.783 38.2383C281.14 37.5612 280.818 36.7656 280.818 35.8516C280.818 34.9375 281.14 34.1589 281.783 33.5156C282.426 32.8385 283.239 32.5 284.221 32.5H287.268C288.249 32.5 289.062 32.8385 289.705 33.5156C290.348 34.1589 290.67 34.9375 290.67 35.8516C290.67 36.7656 290.348 37.5612 289.705 38.2383C289.062 38.8815 288.249 39.2031 287.268 39.2031H284.221ZM297.83 39.2031C296.577 39.2031 295.596 38.8815 294.885 38.2383C294.174 37.5612 293.818 36.7656 293.818 35.8516C293.818 34.9375 294.174 34.1589 294.885 33.5156C295.596 32.8385 296.577 32.5 297.83 32.5H306.158C307.411 32.5 308.393 32.8385 309.104 33.5156C309.814 34.1589 310.17 34.9375 310.17 35.8516C310.17 36.7656 309.814 37.5612 309.104 38.2383C308.393 38.8815 307.411 39.2031 306.158 39.2031H297.83ZM265.33 32.7031C264.077 32.7031 263.096 32.3815 262.385 31.7383C261.674 31.0612 261.318 30.2656 261.318 29.3516C261.318 28.4375 261.674 27.6589 262.385 27.0156C263.096 26.3385 264.077 26 265.33 26H306.158C307.411 26 308.393 26.3385 309.104 27.0156C309.814 27.6589 310.17 28.4375 310.17 29.3516C310.17 30.2656 309.814 31.0612 309.104 31.7383C308.393 32.3815 307.411 32.7031 306.158 32.7031H265.33ZM265.33 26.2031C264.077 26.2031 263.096 25.8815 262.385 25.2383C261.674 24.5612 261.318 23.7656 261.318 22.8516C261.318 21.9375 261.674 21.1589 262.385 20.5156C263.096 19.8385 264.077 19.5 265.33 19.5H306.158C307.411 19.5 308.393 19.8385 309.104 20.5156C309.814 21.1589 310.17 21.9375 310.17 22.8516C310.17 23.7656 309.814 24.5612 309.104 25.2383C308.393 25.8815 307.411 26.2031 306.158 26.2031H265.33ZM265.33 19.7031C264.077 19.7031 263.096 19.3815 262.385 18.7383C261.674 18.0612 261.318 17.2656 261.318 16.3516C261.318 15.4375 261.674 14.6589 262.385 14.0156C263.096 13.3385 264.077 13 265.33 13H273.658C274.911 13 275.893 13.3385 276.604 14.0156C277.314 14.6589 277.67 15.4375 277.67 16.3516C277.67 17.2656 277.314 18.0612 276.604 18.7383C275.893 19.3815 274.911 19.7031 273.658 19.7031H265.33ZM291.33 19.7031C290.077 19.7031 289.096 19.3815 288.385 18.7383C287.674 18.0612 287.318 17.2656 287.318 16.3516C287.318 15.4375 287.674 14.6589 288.385 14.0156C289.096 13.3385 290.077 13 291.33 13H299.658C300.911 13 301.893 13.3385 302.604 14.0156C303.314 14.6589 303.67 15.4375 303.67 16.3516C303.67 17.2656 303.314 18.0612 302.604 18.7383C301.893 19.3815 300.911 19.7031 299.658 19.7031H291.33ZM324.094 45.7031C322.841 45.7031 321.859 45.3815 321.148 44.7383C320.438 44.0612 320.082 43.2656 320.082 42.3516C320.082 41.4375 320.438 40.6589 321.148 40.0156C321.859 39.3385 322.841 39 324.094 39H351.922C353.174 39 354.156 39.3385 354.867 40.0156C355.578 40.6589 355.934 41.4375 355.934 42.3516C355.934 43.2656 355.578 44.0612 354.867 44.7383C354.156 45.3815 353.174 45.7031 351.922 45.7031H324.094ZM317.594 39.2031C316.341 39.2031 315.359 38.8815 314.648 38.2383C313.938 37.5612 313.582 36.7656 313.582 35.8516C313.582 34.9375 313.938 34.1589 314.648 33.5156C315.359 32.8385 316.341 32.5 317.594 32.5H325.922C327.174 32.5 328.156 32.8385 328.867 33.5156C329.578 34.1589 329.934 34.9375 329.934 35.8516C329.934 36.7656 329.578 37.5612 328.867 38.2383C328.156 38.8815 327.174 39.2031 325.922 39.2031H317.594ZM343.594 39.2031C342.341 39.2031 341.359 38.8815 340.648 38.2383C339.938 37.5612 339.582 36.7656 339.582 35.8516C339.582 34.9375 339.938 34.1589 340.648 33.5156C341.359 32.8385 342.341 32.5 343.594 32.5H351.922C353.174 32.5 354.156 32.8385 354.867 33.5156C355.578 34.1589 355.934 34.9375 355.934 35.8516C355.934 36.7656 355.578 37.5612 354.867 38.2383C354.156 38.8815 353.174 39.2031 351.922 39.2031H343.594ZM317.594 32.7031C316.341 32.7031 315.359 32.3815 314.648 31.7383C313.938 31.0612 313.582 30.2656 313.582 29.3516C313.582 28.4375 313.938 27.6589 314.648 27.0156C315.359 26.3385 316.341 26 317.594 26H325.922C327.174 26 328.156 26.3385 328.867 27.0156C329.578 27.6589 329.934 28.4375 329.934 29.3516C329.934 30.2656 329.578 31.0612 328.867 31.7383C328.156 32.3815 327.174 32.7031 325.922 32.7031H317.594ZM343.594 32.7031C342.341 32.7031 341.359 32.3815 340.648 31.7383C339.938 31.0612 339.582 30.2656 339.582 29.3516C339.582 28.4375 339.938 27.6589 340.648 27.0156C341.359 26.3385 342.341 26 343.594 26H351.922C353.174 26 354.156 26.3385 354.867 27.0156C355.578 27.6589 355.934 28.4375 355.934 29.3516C355.934 30.2656 355.578 31.0612 354.867 31.7383C354.156 32.3815 353.174 32.7031 351.922 32.7031H343.594ZM317.594 26.2031C316.341 26.2031 315.359 25.8815 314.648 25.2383C313.938 24.5612 313.582 23.7656 313.582 22.8516C313.582 21.9375 313.938 21.1589 314.648 20.5156C315.359 19.8385 316.341 19.5 317.594 19.5H325.922C327.174 19.5 328.156 19.8385 328.867 20.5156C329.578 21.1589 329.934 21.9375 329.934 22.8516C329.934 23.7656 329.578 24.5612 328.867 25.2383C328.156 25.8815 327.174 26.2031 325.922 26.2031H317.594ZM343.594 26.2031C342.341 26.2031 341.359 25.8815 340.648 25.2383C339.938 24.5612 339.582 23.7656 339.582 22.8516C339.582 21.9375 339.938 21.1589 340.648 20.5156C341.359 19.8385 342.341 19.5 343.594 19.5H351.922C353.174 19.5 354.156 19.8385 354.867 20.5156C355.578 21.1589 355.934 21.9375 355.934 22.8516C355.934 23.7656 355.578 24.5612 354.867 25.2383C354.156 25.8815 353.174 26.2031 351.922 26.2031H343.594ZM317.594 19.7031C316.341 19.7031 315.359 19.3815 314.648 18.7383C313.938 18.0612 313.582 17.2656 313.582 16.3516C313.582 15.4375 313.938 14.6589 314.648 14.0156C315.359 13.3385 316.341 13 317.594 13H325.922C327.174 13 328.156 13.3385 328.867 14.0156C329.578 14.6589 329.934 15.4375 329.934 16.3516C329.934 17.2656 329.578 18.0612 328.867 18.7383C328.156 19.3815 327.174 19.7031 325.922 19.7031H317.594ZM343.594 19.7031C342.341 19.7031 341.359 19.3815 340.648 18.7383C339.938 18.0612 339.582 17.2656 339.582 16.3516C339.582 15.4375 339.938 14.6589 340.648 14.0156C341.359 13.3385 342.341 13 343.594 13H351.922C353.174 13 354.156 13.3385 354.867 14.0156C355.578 14.6589 355.934 15.4375 355.934 16.3516C355.934 17.2656 355.578 18.0612 354.867 18.7383C354.156 19.3815 353.174 19.7031 351.922 19.7031H343.594ZM369.857 45.7031C368.605 45.7031 367.623 45.3815 366.912 44.7383C366.201 44.0612 365.846 43.2656 365.846 42.3516C365.846 41.4375 366.201 40.6589 366.912 40.0156C367.623 39.3385 368.605 39 369.857 39H378.186C379.438 39 380.42 39.3385 381.131 40.0156C381.842 40.6589 382.197 41.4375 382.197 42.3516C382.197 43.2656 381.842 44.0612 381.131 44.7383C380.42 45.3815 379.438 45.7031 378.186 45.7031H369.857ZM402.357 45.7031C401.105 45.7031 400.123 45.3815 399.412 44.7383C398.701 44.0612 398.346 43.2656 398.346 42.3516C398.346 41.4375 398.701 40.6589 399.412 40.0156C400.123 39.3385 401.105 39 402.357 39H410.686C411.938 39 412.92 39.3385 413.631 40.0156C414.342 40.6589 414.697 41.4375 414.697 42.3516C414.697 43.2656 414.342 44.0612 413.631 44.7383C412.92 45.3815 411.938 45.7031 410.686 45.7031H402.357ZM369.857 39.2031C368.605 39.2031 367.623 38.8815 366.912 38.2383C366.201 37.5612 365.846 36.7656 365.846 35.8516C365.846 34.9375 366.201 34.1589 366.912 33.5156C367.623 32.8385 368.605 32.5 369.857 32.5H378.186C379.438 32.5 380.42 32.8385 381.131 33.5156C381.842 34.1589 382.197 34.9375 382.197 35.8516C382.197 36.7656 381.842 37.5612 381.131 38.2383C380.42 38.8815 379.438 39.2031 378.186 39.2031H369.857ZM388.748 39.2031C387.766 39.2031 386.954 38.8815 386.311 38.2383C385.667 37.5612 385.346 36.7656 385.346 35.8516C385.346 34.9375 385.667 34.1589 386.311 33.5156C386.954 32.8385 387.766 32.5 388.748 32.5H391.795C392.777 32.5 393.589 32.8385 394.232 33.5156C394.876 34.1589 395.197 34.9375 395.197 35.8516C395.197 36.7656 394.876 37.5612 394.232 38.2383C393.589 38.8815 392.777 39.2031 391.795 39.2031H388.748ZM402.357 39.2031C401.105 39.2031 400.123 38.8815 399.412 38.2383C398.701 37.5612 398.346 36.7656 398.346 35.8516C398.346 34.9375 398.701 34.1589 399.412 33.5156C400.123 32.8385 401.105 32.5 402.357 32.5H410.686C411.938 32.5 412.92 32.8385 413.631 33.5156C414.342 34.1589 414.697 34.9375 414.697 35.8516C414.697 36.7656 414.342 37.5612 413.631 38.2383C412.92 38.8815 411.938 39.2031 410.686 39.2031H402.357ZM369.857 32.7031C368.605 32.7031 367.623 32.3815 366.912 31.7383C366.201 31.0612 365.846 30.2656 365.846 29.3516C365.846 28.4375 366.201 27.6589 366.912 27.0156C367.623 26.3385 368.605 26 369.857 26H410.686C411.938 26 412.92 26.3385 413.631 27.0156C414.342 27.6589 414.697 28.4375 414.697 29.3516C414.697 30.2656 414.342 31.0612 413.631 31.7383C412.92 32.3815 411.938 32.7031 410.686 32.7031H369.857ZM369.857 26.2031C368.605 26.2031 367.623 25.8815 366.912 25.2383C366.201 24.5612 365.846 23.7656 365.846 22.8516C365.846 21.9375 366.201 21.1589 366.912 20.5156C367.623 19.8385 368.605 19.5 369.857 19.5H410.686C411.938 19.5 412.92 19.8385 413.631 20.5156C414.342 21.1589 414.697 21.9375 414.697 22.8516C414.697 23.7656 414.342 24.5612 413.631 25.2383C412.92 25.8815 411.938 26.2031 410.686 26.2031H369.857ZM369.857 19.7031C368.605 19.7031 367.623 19.3815 366.912 18.7383C366.201 18.0612 365.846 17.2656 365.846 16.3516C365.846 15.4375 366.201 14.6589 366.912 14.0156C367.623 13.3385 368.605 13 369.857 13H378.186C379.438 13 380.42 13.3385 381.131 14.0156C381.842 14.6589 382.197 15.4375 382.197 16.3516C382.197 17.2656 381.842 18.0612 381.131 18.7383C380.42 19.3815 379.438 19.7031 378.186 19.7031H369.857ZM395.857 19.7031C394.605 19.7031 393.623 19.3815 392.912 18.7383C392.201 18.0612 391.846 17.2656 391.846 16.3516C391.846 15.4375 392.201 14.6589 392.912 14.0156C393.623 13.3385 394.605 13 395.857 13H404.186C405.438 13 406.42 13.3385 407.131 14.0156C407.842 14.6589 408.197 15.4375 408.197 16.3516C408.197 17.2656 407.842 18.0612 407.131 18.7383C406.42 19.3815 405.438 19.7031 404.186 19.7031H395.857ZM428.621 45.7031C427.368 45.7031 426.387 45.3815 425.676 44.7383C424.965 44.0612 424.609 43.2656 424.609 42.3516C424.609 41.4375 424.965 40.6589 425.676 40.0156C426.387 39.3385 427.368 39 428.621 39H456.449C457.702 39 458.684 39.3385 459.395 40.0156C460.105 40.6589 460.461 41.4375 460.461 42.3516C460.461 43.2656 460.105 44.0612 459.395 44.7383C458.684 45.3815 457.702 45.7031 456.449 45.7031H428.621ZM422.121 39.2031C420.868 39.2031 419.887 38.8815 419.176 38.2383C418.465 37.5612 418.109 36.7656 418.109 35.8516C418.109 34.9375 418.465 34.1589 419.176 33.5156C419.887 32.8385 420.868 32.5 422.121 32.5H430.449C431.702 32.5 432.684 32.8385 433.395 33.5156C434.105 34.1589 434.461 34.9375 434.461 35.8516C434.461 36.7656 434.105 37.5612 433.395 38.2383C432.684 38.8815 431.702 39.2031 430.449 39.2031H422.121ZM448.121 39.2031C446.868 39.2031 445.887 38.8815 445.176 38.2383C444.465 37.5612 444.109 36.7656 444.109 35.8516C444.109 34.9375 444.465 34.1589 445.176 33.5156C445.887 32.8385 446.868 32.5 448.121 32.5H456.449C457.702 32.5 458.684 32.8385 459.395 33.5156C460.105 34.1589 460.461 34.9375 460.461 35.8516C460.461 36.7656 460.105 37.5612 459.395 38.2383C458.684 38.8815 457.702 39.2031 456.449 39.2031H448.121ZM428.621 32.7031C427.368 32.7031 426.387 32.3815 425.676 31.7383C424.965 31.0612 424.609 30.2656 424.609 29.3516C424.609 28.4375 424.965 27.6589 425.676 27.0156C426.387 26.3385 427.368 26 428.621 26H456.449C457.702 26 458.684 26.3385 459.395 27.0156C460.105 27.6589 460.461 28.4375 460.461 29.3516C460.461 30.2656 460.105 31.0612 459.395 31.7383C458.684 32.3815 457.702 32.7031 456.449 32.7031H428.621ZM448.121 26.2031C446.868 26.2031 445.887 25.8815 445.176 25.2383C444.465 24.5612 444.109 23.7656 444.109 22.8516C444.109 21.9375 444.465 21.1589 445.176 20.5156C445.887 19.8385 446.868 19.5 448.121 19.5H456.449C457.702 19.5 458.684 19.8385 459.395 20.5156C460.105 21.1589 460.461 21.9375 460.461 22.8516C460.461 23.7656 460.105 24.5612 459.395 25.2383C458.684 25.8815 457.702 26.2031 456.449 26.2031H448.121ZM428.621 19.7031C427.368 19.7031 426.387 19.3815 425.676 18.7383C424.965 18.0612 424.609 17.2656 424.609 16.3516C424.609 15.4375 424.965 14.6589 425.676 14.0156C426.387 13.3385 427.368 13 428.621 13H449.949C451.202 13 452.184 13.3385 452.895 14.0156C453.605 14.6589 453.961 15.4375 453.961 16.3516C453.961 17.2656 453.605 18.0612 452.895 18.7383C452.184 19.3815 451.202 19.7031 449.949 19.7031H428.621ZM474.385 45.7031C473.132 45.7031 472.15 45.3815 471.439 44.7383C470.729 44.0612 470.373 43.2656 470.373 42.3516C470.373 41.4375 470.729 40.6589 471.439 40.0156C472.15 39.3385 473.132 39 474.385 39H482.713C483.965 39 484.947 39.3385 485.658 40.0156C486.369 40.6589 486.725 41.4375 486.725 42.3516C486.725 43.2656 486.369 44.0612 485.658 44.7383C484.947 45.3815 483.965 45.7031 482.713 45.7031H474.385ZM500.385 45.7031C499.132 45.7031 498.15 45.3815 497.439 44.7383C496.729 44.0612 496.373 43.2656 496.373 42.3516C496.373 41.4375 496.729 40.6589 497.439 40.0156C498.15 39.3385 499.132 39 500.385 39H508.713C509.965 39 510.947 39.3385 511.658 40.0156C512.369 40.6589 512.725 41.4375 512.725 42.3516C512.725 43.2656 512.369 44.0612 511.658 44.7383C510.947 45.3815 509.965 45.7031 508.713 45.7031H500.385ZM474.385 39.2031C473.132 39.2031 472.15 38.8815 471.439 38.2383C470.729 37.5612 470.373 36.7656 470.373 35.8516C470.373 34.9375 470.729 34.1589 471.439 33.5156C472.15 32.8385 473.132 32.5 474.385 32.5H482.713C483.965 32.5 484.947 32.8385 485.658 33.5156C486.369 34.1589 486.725 34.9375 486.725 35.8516C486.725 36.7656 486.369 37.5612 485.658 38.2383C484.947 38.8815 483.965 39.2031 482.713 39.2031H474.385ZM500.385 39.2031C499.132 39.2031 498.15 38.8815 497.439 38.2383C496.729 37.5612 496.373 36.7656 496.373 35.8516C496.373 34.9375 496.729 34.1589 497.439 33.5156C498.15 32.8385 499.132 32.5 500.385 32.5H508.713C509.965 32.5 510.947 32.8385 511.658 33.5156C512.369 34.1589 512.725 34.9375 512.725 35.8516C512.725 36.7656 512.369 37.5612 511.658 38.2383C510.947 38.8815 509.965 39.2031 508.713 39.2031H500.385ZM474.385 32.7031C473.132 32.7031 472.15 32.3815 471.439 31.7383C470.729 31.0612 470.373 30.2656 470.373 29.3516C470.373 28.4375 470.729 27.6589 471.439 27.0156C472.15 26.3385 473.132 26 474.385 26H482.713C483.965 26 484.947 26.3385 485.658 27.0156C486.369 27.6589 486.725 28.4375 486.725 29.3516C486.725 30.2656 486.369 31.0612 485.658 31.7383C484.947 32.3815 483.965 32.7031 482.713 32.7031H474.385ZM500.385 32.7031C499.132 32.7031 498.15 32.3815 497.439 31.7383C496.729 31.0612 496.373 30.2656 496.373 29.3516C496.373 28.4375 496.729 27.6589 497.439 27.0156C498.15 26.3385 499.132 26 500.385 26H508.713C509.965 26 510.947 26.3385 511.658 27.0156C512.369 27.6589 512.725 28.4375 512.725 29.3516C512.725 30.2656 512.369 31.0612 511.658 31.7383C510.947 32.3815 509.965 32.7031 508.713 32.7031H500.385ZM474.385 26.2031C473.132 26.2031 472.15 25.8815 471.439 25.2383C470.729 24.5612 470.373 23.7656 470.373 22.8516C470.373 21.9375 470.729 21.1589 471.439 20.5156C472.15 19.8385 473.132 19.5 474.385 19.5H482.713C483.965 19.5 484.947 19.8385 485.658 20.5156C486.369 21.1589 486.725 21.9375 486.725 22.8516C486.725 23.7656 486.369 24.5612 485.658 25.2383C484.947 25.8815 483.965 26.2031 482.713 26.2031H474.385ZM500.385 26.2031C499.132 26.2031 498.15 25.8815 497.439 25.2383C496.729 24.5612 496.373 23.7656 496.373 22.8516C496.373 21.9375 496.729 21.1589 497.439 20.5156C498.15 19.8385 499.132 19.5 500.385 19.5H508.713C509.965 19.5 510.947 19.8385 511.658 20.5156C512.369 21.1589 512.725 21.9375 512.725 22.8516C512.725 23.7656 512.369 24.5612 511.658 25.2383C510.947 25.8815 509.965 26.2031 508.713 26.2031H500.385ZM474.385 19.7031C473.132 19.7031 472.15 19.3815 471.439 18.7383C470.729 18.0612 470.373 17.2656 470.373 16.3516C470.373 15.4375 470.729 14.6589 471.439 14.0156C472.15 13.3385 473.132 13 474.385 13H502.213C503.465 13 504.447 13.3385 505.158 14.0156C505.869 14.6589 506.225 15.4375 506.225 16.3516C506.225 17.2656 505.869 18.0612 505.158 18.7383C504.447 19.3815 503.465 19.7031 502.213 19.7031H474.385ZM578.912 45.7031C577.66 45.7031 576.678 45.3815 575.967 44.7383C575.256 44.0612 574.9 43.2656 574.9 42.3516C574.9 41.4375 575.256 40.6589 575.967 40.0156C576.678 39.3385 577.66 39 578.912 39H587.24C588.493 39 589.475 39.3385 590.186 40.0156C590.896 40.6589 591.252 41.4375 591.252 42.3516C591.252 43.2656 590.896 44.0612 590.186 44.7383C589.475 45.3815 588.493 45.7031 587.24 45.7031H578.912ZM578.912 39.2031C577.66 39.2031 576.678 38.8815 575.967 38.2383C575.256 37.5612 574.9 36.7656 574.9 35.8516C574.9 34.9375 575.256 34.1589 575.967 33.5156C576.678 32.8385 577.66 32.5 578.912 32.5H587.24C588.493 32.5 589.475 32.8385 590.186 33.5156C590.896 34.1589 591.252 34.9375 591.252 35.8516C591.252 36.7656 590.896 37.5612 590.186 38.2383C589.475 38.8815 588.493 39.2031 587.24 39.2031H578.912ZM578.912 32.7031C577.66 32.7031 576.678 32.3815 575.967 31.7383C575.256 31.0612 574.9 30.2656 574.9 29.3516C574.9 28.4375 575.256 27.6589 575.967 27.0156C576.678 26.3385 577.66 26 578.912 26H587.24C588.493 26 589.475 26.3385 590.186 27.0156C590.896 27.6589 591.252 28.4375 591.252 29.3516C591.252 30.2656 590.896 31.0612 590.186 31.7383C589.475 32.3815 588.493 32.7031 587.24 32.7031H578.912ZM578.912 26.2031C577.66 26.2031 576.678 25.8815 575.967 25.2383C575.256 24.5612 574.9 23.7656 574.9 22.8516C574.9 21.9375 575.256 21.1589 575.967 20.5156C576.678 19.8385 577.66 19.5 578.912 19.5H606.74C607.993 19.5 608.975 19.8385 609.686 20.5156C610.396 21.1589 610.752 21.9375 610.752 22.8516C610.752 23.7656 610.396 24.5612 609.686 25.2383C608.975 25.8815 607.993 26.2031 606.74 26.2031H578.912ZM578.912 19.7031C577.66 19.7031 576.678 19.3815 575.967 18.7383C575.256 18.0612 574.9 17.2656 574.9 16.3516C574.9 15.4375 575.256 14.6589 575.967 14.0156C576.678 13.3385 577.66 13 578.912 13H587.24C588.493 13 589.475 13.3385 590.186 14.0156C590.896 14.6589 591.252 15.4375 591.252 16.3516C591.252 17.2656 590.896 18.0612 590.186 18.7383C589.475 19.3815 588.493 19.7031 587.24 19.7031H578.912ZM604.912 19.7031C603.66 19.7031 602.678 19.3815 601.967 18.7383C601.256 18.0612 600.9 17.2656 600.9 16.3516C600.9 15.4375 601.256 14.6589 601.967 14.0156C602.678 13.3385 603.66 13 604.912 13H613.24C614.493 13 615.475 13.3385 616.186 14.0156C616.896 14.6589 617.252 15.4375 617.252 16.3516C617.252 17.2656 616.896 18.0612 616.186 18.7383C615.475 19.3815 614.493 19.7031 613.24 19.7031H604.912ZM578.912 13.2031C577.66 13.2031 576.678 12.8815 575.967 12.2383C575.256 11.5612 574.9 10.7656 574.9 9.85156C574.9 8.9375 575.256 8.15885 575.967 7.51562C576.678 6.83854 577.66 6.5 578.912 6.5H587.24C588.493 6.5 589.475 6.83854 590.186 7.51562C590.896 8.15885 591.252 8.9375 591.252 9.85156C591.252 10.7656 590.896 11.5612 590.186 12.2383C589.475 12.8815 588.493 13.2031 587.24 13.2031H578.912ZM604.912 13.2031C603.66 13.2031 602.678 12.8815 601.967 12.2383C601.256 11.5612 600.9 10.7656 600.9 9.85156C600.9 8.9375 601.256 8.15885 601.967 7.51562C602.678 6.83854 603.66 6.5 604.912 6.5H613.24C614.493 6.5 615.475 6.83854 616.186 7.51562C616.896 8.15885 617.252 8.9375 617.252 9.85156C617.252 10.7656 616.896 11.5612 616.186 12.2383C615.475 12.8815 614.493 13.2031 613.24 13.2031H604.912ZM578.912 6.70312C577.66 6.70312 576.678 6.38151 575.967 5.73828C575.256 5.0612 574.9 4.26562 574.9 3.35156C574.9 2.4375 575.256 1.65885 575.967 1.01562C576.678 0.338542 577.66 0 578.912 0H606.74C607.993 0 608.975 0.338542 609.686 1.01562C610.396 1.65885 610.752 2.4375 610.752 3.35156C610.752 4.26562 610.396 5.0612 609.686 5.73828C608.975 6.38151 607.993 6.70312 606.74 6.70312H578.912ZM637.676 45.7031C636.423 45.7031 635.441 45.3815 634.73 44.7383C634.02 44.0612 633.664 43.2656 633.664 42.3516C633.664 41.4375 634.02 40.6589 634.73 40.0156C635.441 39.3385 636.423 39 637.676 39H659.004C660.257 39 661.238 39.3385 661.949 40.0156C662.66 40.6589 663.016 41.4375 663.016 42.3516C663.016 43.2656 662.66 44.0612 661.949 44.7383C661.238 45.3815 660.257 45.7031 659.004 45.7031H637.676ZM631.176 39.2031C629.923 39.2031 628.941 38.8815 628.23 38.2383C627.52 37.5612 627.164 36.7656 627.164 35.8516C627.164 34.9375 627.52 34.1589 628.23 33.5156C628.941 32.8385 629.923 32.5 631.176 32.5H639.504C640.757 32.5 641.738 32.8385 642.449 33.5156C643.16 34.1589 643.516 34.9375 643.516 35.8516C643.516 36.7656 643.16 37.5612 642.449 38.2383C641.738 38.8815 640.757 39.2031 639.504 39.2031H631.176ZM631.176 32.7031C629.923 32.7031 628.941 32.3815 628.23 31.7383C627.52 31.0612 627.164 30.2656 627.164 29.3516C627.164 28.4375 627.52 27.6589 628.23 27.0156C628.941 26.3385 629.923 26 631.176 26H665.504C666.757 26 667.738 26.3385 668.449 27.0156C669.16 27.6589 669.516 28.4375 669.516 29.3516C669.516 30.2656 669.16 31.0612 668.449 31.7383C667.738 32.3815 666.757 32.7031 665.504 32.7031H631.176ZM631.176 26.2031C629.923 26.2031 628.941 25.8815 628.23 25.2383C627.52 24.5612 627.164 23.7656 627.164 22.8516C627.164 21.9375 627.52 21.1589 628.23 20.5156C628.941 19.8385 629.923 19.5 631.176 19.5H639.504C640.757 19.5 641.738 19.8385 642.449 20.5156C643.16 21.1589 643.516 21.9375 643.516 22.8516C643.516 23.7656 643.16 24.5612 642.449 25.2383C641.738 25.8815 640.757 26.2031 639.504 26.2031H631.176ZM657.176 26.2031C655.923 26.2031 654.941 25.8815 654.23 25.2383C653.52 24.5612 653.164 23.7656 653.164 22.8516C653.164 21.9375 653.52 21.1589 654.23 20.5156C654.941 19.8385 655.923 19.5 657.176 19.5H665.504C666.757 19.5 667.738 19.8385 668.449 20.5156C669.16 21.1589 669.516 21.9375 669.516 22.8516C669.516 23.7656 669.16 24.5612 668.449 25.2383C667.738 25.8815 666.757 26.2031 665.504 26.2031H657.176ZM637.676 19.7031C636.423 19.7031 635.441 19.3815 634.73 18.7383C634.02 18.0612 633.664 17.2656 633.664 16.3516C633.664 15.4375 634.02 14.6589 634.73 14.0156C635.441 13.3385 636.423 13 637.676 13H659.004C660.257 13 661.238 13.3385 661.949 14.0156C662.66 14.6589 663.016 15.4375 663.016 16.3516C663.016 17.2656 662.66 18.0612 661.949 18.7383C661.238 19.3815 660.257 19.7031 659.004 19.7031H637.676ZM683.439 45.7031C682.187 45.7031 681.205 45.3815 680.494 44.7383C679.783 44.0612 679.428 43.2656 679.428 42.3516C679.428 41.4375 679.783 40.6589 680.494 40.0156C681.205 39.3385 682.187 39 683.439 39H691.768C693.02 39 694.002 39.3385 694.713 40.0156C695.424 40.6589 695.779 41.4375 695.779 42.3516C695.779 43.2656 695.424 44.0612 694.713 44.7383C694.002 45.3815 693.02 45.7031 691.768 45.7031H683.439ZM715.939 45.7031C714.687 45.7031 713.705 45.3815 712.994 44.7383C712.283 44.0612 711.928 43.2656 711.928 42.3516C711.928 41.4375 712.283 40.6589 712.994 40.0156C713.705 39.3385 714.687 39 715.939 39H724.268C725.52 39 726.502 39.3385 727.213 40.0156C727.924 40.6589 728.279 41.4375 728.279 42.3516C728.279 43.2656 727.924 44.0612 727.213 44.7383C726.502 45.3815 725.52 45.7031 724.268 45.7031H715.939ZM683.439 39.2031C682.187 39.2031 681.205 38.8815 680.494 38.2383C679.783 37.5612 679.428 36.7656 679.428 35.8516C679.428 34.9375 679.783 34.1589 680.494 33.5156C681.205 32.8385 682.187 32.5 683.439 32.5H691.768C693.02 32.5 694.002 32.8385 694.713 33.5156C695.424 34.1589 695.779 34.9375 695.779 35.8516C695.779 36.7656 695.424 37.5612 694.713 38.2383C694.002 38.8815 693.02 39.2031 691.768 39.2031H683.439ZM702.33 39.2031C701.348 39.2031 700.536 38.8815 699.893 38.2383C699.249 37.5612 698.928 36.7656 698.928 35.8516C698.928 34.9375 699.249 34.1589 699.893 33.5156C700.536 32.8385 701.348 32.5 702.33 32.5H705.377C706.359 32.5 707.171 32.8385 707.814 33.5156C708.458 34.1589 708.779 34.9375 708.779 35.8516C708.779 36.7656 708.458 37.5612 707.814 38.2383C707.171 38.8815 706.359 39.2031 705.377 39.2031H702.33ZM715.939 39.2031C714.687 39.2031 713.705 38.8815 712.994 38.2383C712.283 37.5612 711.928 36.7656 711.928 35.8516C711.928 34.9375 712.283 34.1589 712.994 33.5156C713.705 32.8385 714.687 32.5 715.939 32.5H724.268C725.52 32.5 726.502 32.8385 727.213 33.5156C727.924 34.1589 728.279 34.9375 728.279 35.8516C728.279 36.7656 727.924 37.5612 727.213 38.2383C726.502 38.8815 725.52 39.2031 724.268 39.2031H715.939ZM683.439 32.7031C682.187 32.7031 681.205 32.3815 680.494 31.7383C679.783 31.0612 679.428 30.2656 679.428 29.3516C679.428 28.4375 679.783 27.6589 680.494 27.0156C681.205 26.3385 682.187 26 683.439 26H724.268C725.52 26 726.502 26.3385 727.213 27.0156C727.924 27.6589 728.279 28.4375 728.279 29.3516C728.279 30.2656 727.924 31.0612 727.213 31.7383C726.502 32.3815 725.52 32.7031 724.268 32.7031H683.439ZM683.439 26.2031C682.187 26.2031 681.205 25.8815 680.494 25.2383C679.783 24.5612 679.428 23.7656 679.428 22.8516C679.428 21.9375 679.783 21.1589 680.494 20.5156C681.205 19.8385 682.187 19.5 683.439 19.5H724.268C725.52 19.5 726.502 19.8385 727.213 20.5156C727.924 21.1589 728.279 21.9375 728.279 22.8516C728.279 23.7656 727.924 24.5612 727.213 25.2383C726.502 25.8815 725.52 26.2031 724.268 26.2031H683.439ZM683.439 19.7031C682.187 19.7031 681.205 19.3815 680.494 18.7383C679.783 18.0612 679.428 17.2656 679.428 16.3516C679.428 15.4375 679.783 14.6589 680.494 14.0156C681.205 13.3385 682.187 13 683.439 13H691.768C693.02 13 694.002 13.3385 694.713 14.0156C695.424 14.6589 695.779 15.4375 695.779 16.3516C695.779 17.2656 695.424 18.0612 694.713 18.7383C694.002 19.3815 693.02 19.7031 691.768 19.7031H683.439ZM709.439 19.7031C708.187 19.7031 707.205 19.3815 706.494 18.7383C705.783 18.0612 705.428 17.2656 705.428 16.3516C705.428 15.4375 705.783 14.6589 706.494 14.0156C707.205 13.3385 708.187 13 709.439 13H717.768C719.02 13 720.002 13.3385 720.713 14.0156C721.424 14.6589 721.779 15.4375 721.779 16.3516C721.779 17.2656 721.424 18.0612 720.713 18.7383C720.002 19.3815 719.02 19.7031 717.768 19.7031H709.439ZM742.203 45.7031C740.951 45.7031 739.969 45.3815 739.258 44.7383C738.547 44.0612 738.191 43.2656 738.191 42.3516C738.191 41.4375 738.547 40.6589 739.258 40.0156C739.969 39.3385 740.951 39 742.203 39H763.531C764.784 39 765.766 39.3385 766.477 40.0156C767.188 40.6589 767.543 41.4375 767.543 42.3516C767.543 43.2656 767.188 44.0612 766.477 44.7383C765.766 45.3815 764.784 45.7031 763.531 45.7031H742.203ZM735.703 39.2031C734.451 39.2031 733.469 38.8815 732.758 38.2383C732.047 37.5612 731.691 36.7656 731.691 35.8516C731.691 34.9375 732.047 34.1589 732.758 33.5156C733.469 32.8385 734.451 32.5 735.703 32.5H744.031C745.284 32.5 746.266 32.8385 746.977 33.5156C747.688 34.1589 748.043 34.9375 748.043 35.8516C748.043 36.7656 747.688 37.5612 746.977 38.2383C746.266 38.8815 745.284 39.2031 744.031 39.2031H735.703ZM735.703 32.7031C734.451 32.7031 733.469 32.3815 732.758 31.7383C732.047 31.0612 731.691 30.2656 731.691 29.3516C731.691 28.4375 732.047 27.6589 732.758 27.0156C733.469 26.3385 734.451 26 735.703 26H770.031C771.284 26 772.266 26.3385 772.977 27.0156C773.688 27.6589 774.043 28.4375 774.043 29.3516C774.043 30.2656 773.688 31.0612 772.977 31.7383C772.266 32.3815 771.284 32.7031 770.031 32.7031H735.703ZM735.703 26.2031C734.451 26.2031 733.469 25.8815 732.758 25.2383C732.047 24.5612 731.691 23.7656 731.691 22.8516C731.691 21.9375 732.047 21.1589 732.758 20.5156C733.469 19.8385 734.451 19.5 735.703 19.5H744.031C745.284 19.5 746.266 19.8385 746.977 20.5156C747.688 21.1589 748.043 21.9375 748.043 22.8516C748.043 23.7656 747.688 24.5612 746.977 25.2383C746.266 25.8815 745.284 26.2031 744.031 26.2031H735.703ZM761.703 26.2031C760.451 26.2031 759.469 25.8815 758.758 25.2383C758.047 24.5612 757.691 23.7656 757.691 22.8516C757.691 21.9375 758.047 21.1589 758.758 20.5156C759.469 19.8385 760.451 19.5 761.703 19.5H770.031C771.284 19.5 772.266 19.8385 772.977 20.5156C773.688 21.1589 774.043 21.9375 774.043 22.8516C774.043 23.7656 773.688 24.5612 772.977 25.2383C772.266 25.8815 771.284 26.2031 770.031 26.2031H761.703ZM742.203 19.7031C740.951 19.7031 739.969 19.3815 739.258 18.7383C738.547 18.0612 738.191 17.2656 738.191 16.3516C738.191 15.4375 738.547 14.6589 739.258 14.0156C739.969 13.3385 740.951 13 742.203 13H763.531C764.784 13 765.766 13.3385 766.477 14.0156C767.188 14.6589 767.543 15.4375 767.543 16.3516C767.543 17.2656 767.188 18.0612 766.477 18.7383C765.766 19.3815 764.784 19.7031 763.531 19.7031H742.203ZM787.967 45.7031C786.714 45.7031 785.732 45.3815 785.021 44.7383C784.311 44.0612 783.955 43.2656 783.955 42.3516C783.955 41.4375 784.311 40.6589 785.021 40.0156C785.732 39.3385 786.714 39 787.967 39H796.295C797.548 39 798.529 39.3385 799.24 40.0156C799.951 40.6589 800.307 41.4375 800.307 42.3516C800.307 43.2656 799.951 44.0612 799.24 44.7383C798.529 45.3815 797.548 45.7031 796.295 45.7031H787.967ZM813.967 45.7031C812.714 45.7031 811.732 45.3815 811.021 44.7383C810.311 44.0612 809.955 43.2656 809.955 42.3516C809.955 41.4375 810.311 40.6589 811.021 40.0156C811.732 39.3385 812.714 39 813.967 39H822.295C823.548 39 824.529 39.3385 825.24 40.0156C825.951 40.6589 826.307 41.4375 826.307 42.3516C826.307 43.2656 825.951 44.0612 825.24 44.7383C824.529 45.3815 823.548 45.7031 822.295 45.7031H813.967ZM787.967 39.2031C786.714 39.2031 785.732 38.8815 785.021 38.2383C784.311 37.5612 783.955 36.7656 783.955 35.8516C783.955 34.9375 784.311 34.1589 785.021 33.5156C785.732 32.8385 786.714 32.5 787.967 32.5H796.295C797.548 32.5 798.529 32.8385 799.24 33.5156C799.951 34.1589 800.307 34.9375 800.307 35.8516C800.307 36.7656 799.951 37.5612 799.24 38.2383C798.529 38.8815 797.548 39.2031 796.295 39.2031H787.967ZM813.967 39.2031C812.714 39.2031 811.732 38.8815 811.021 38.2383C810.311 37.5612 809.955 36.7656 809.955 35.8516C809.955 34.9375 810.311 34.1589 811.021 33.5156C811.732 32.8385 812.714 32.5 813.967 32.5H822.295C823.548 32.5 824.529 32.8385 825.24 33.5156C825.951 34.1589 826.307 34.9375 826.307 35.8516C826.307 36.7656 825.951 37.5612 825.24 38.2383C824.529 38.8815 823.548 39.2031 822.295 39.2031H813.967ZM787.967 32.7031C786.714 32.7031 785.732 32.3815 785.021 31.7383C784.311 31.0612 783.955 30.2656 783.955 29.3516C783.955 28.4375 784.311 27.6589 785.021 27.0156C785.732 26.3385 786.714 26 787.967 26H796.295C797.548 26 798.529 26.3385 799.24 27.0156C799.951 27.6589 800.307 28.4375 800.307 29.3516C800.307 30.2656 799.951 31.0612 799.24 31.7383C798.529 32.3815 797.548 32.7031 796.295 32.7031H787.967ZM813.967 32.7031C812.714 32.7031 811.732 32.3815 811.021 31.7383C810.311 31.0612 809.955 30.2656 809.955 29.3516C809.955 28.4375 810.311 27.6589 811.021 27.0156C811.732 26.3385 812.714 26 813.967 26H822.295C823.548 26 824.529 26.3385 825.24 27.0156C825.951 27.6589 826.307 28.4375 826.307 29.3516C826.307 30.2656 825.951 31.0612 825.24 31.7383C824.529 32.3815 823.548 32.7031 822.295 32.7031H813.967ZM787.967 26.2031C786.714 26.2031 785.732 25.8815 785.021 25.2383C784.311 24.5612 783.955 23.7656 783.955 22.8516C783.955 21.9375 784.311 21.1589 785.021 20.5156C785.732 19.8385 786.714 19.5 787.967 19.5H796.295C797.548 19.5 798.529 19.8385 799.24 20.5156C799.951 21.1589 800.307 21.9375 800.307 22.8516C800.307 23.7656 799.951 24.5612 799.24 25.2383C798.529 25.8815 797.548 26.2031 796.295 26.2031H787.967ZM813.967 26.2031C812.714 26.2031 811.732 25.8815 811.021 25.2383C810.311 24.5612 809.955 23.7656 809.955 22.8516C809.955 21.9375 810.311 21.1589 811.021 20.5156C811.732 19.8385 812.714 19.5 813.967 19.5H822.295C823.548 19.5 824.529 19.8385 825.24 20.5156C825.951 21.1589 826.307 21.9375 826.307 22.8516C826.307 23.7656 825.951 24.5612 825.24 25.2383C824.529 25.8815 823.548 26.2031 822.295 26.2031H813.967ZM787.967 19.7031C786.714 19.7031 785.732 19.3815 785.021 18.7383C784.311 18.0612 783.955 17.2656 783.955 16.3516C783.955 15.4375 784.311 14.6589 785.021 14.0156C785.732 13.3385 786.714 13 787.967 13H815.795C817.048 13 818.029 13.3385 818.74 14.0156C819.451 14.6589 819.807 15.4375 819.807 16.3516C819.807 17.2656 819.451 18.0612 818.74 18.7383C818.029 19.3815 817.048 19.7031 815.795 19.7031H787.967ZM846.73 45.7031C845.478 45.7031 844.496 45.3815 843.785 44.7383C843.074 44.0612 842.719 43.2656 842.719 42.3516C842.719 41.4375 843.074 40.6589 843.785 40.0156C844.496 39.3385 845.478 39 846.73 39H874.559C875.811 39 876.793 39.3385 877.504 40.0156C878.215 40.6589 878.57 41.4375 878.57 42.3516C878.57 43.2656 878.215 44.0612 877.504 44.7383C876.793 45.3815 875.811 45.7031 874.559 45.7031H846.73ZM840.23 39.2031C838.978 39.2031 837.996 38.8815 837.285 38.2383C836.574 37.5612 836.219 36.7656 836.219 35.8516C836.219 34.9375 836.574 34.1589 837.285 33.5156C837.996 32.8385 838.978 32.5 840.23 32.5H848.559C849.811 32.5 850.793 32.8385 851.504 33.5156C852.215 34.1589 852.57 34.9375 852.57 35.8516C852.57 36.7656 852.215 37.5612 851.504 38.2383C850.793 38.8815 849.811 39.2031 848.559 39.2031H840.23ZM866.23 39.2031C864.978 39.2031 863.996 38.8815 863.285 38.2383C862.574 37.5612 862.219 36.7656 862.219 35.8516C862.219 34.9375 862.574 34.1589 863.285 33.5156C863.996 32.8385 864.978 32.5 866.23 32.5H874.559C875.811 32.5 876.793 32.8385 877.504 33.5156C878.215 34.1589 878.57 34.9375 878.57 35.8516C878.57 36.7656 878.215 37.5612 877.504 38.2383C876.793 38.8815 875.811 39.2031 874.559 39.2031H866.23ZM846.73 32.7031C845.478 32.7031 844.496 32.3815 843.785 31.7383C843.074 31.0612 842.719 30.2656 842.719 29.3516C842.719 28.4375 843.074 27.6589 843.785 27.0156C844.496 26.3385 845.478 26 846.73 26H874.559C875.811 26 876.793 26.3385 877.504 27.0156C878.215 27.6589 878.57 28.4375 878.57 29.3516C878.57 30.2656 878.215 31.0612 877.504 31.7383C876.793 32.3815 875.811 32.7031 874.559 32.7031H846.73ZM866.23 26.2031C864.978 26.2031 863.996 25.8815 863.285 25.2383C862.574 24.5612 862.219 23.7656 862.219 22.8516C862.219 21.9375 862.574 21.1589 863.285 20.5156C863.996 19.8385 864.978 19.5 866.23 19.5H874.559C875.811 19.5 876.793 19.8385 877.504 20.5156C878.215 21.1589 878.57 21.9375 878.57 22.8516C878.57 23.7656 878.215 24.5612 877.504 25.2383C876.793 25.8815 875.811 26.2031 874.559 26.2031H866.23ZM846.73 19.7031C845.478 19.7031 844.496 19.3815 843.785 18.7383C843.074 18.0612 842.719 17.2656 842.719 16.3516C842.719 15.4375 843.074 14.6589 843.785 14.0156C844.496 13.3385 845.478 13 846.73 13H868.059C869.311 13 870.293 13.3385 871.004 14.0156C871.715 14.6589 872.07 15.4375 872.07 16.3516C872.07 17.2656 871.715 18.0612 871.004 18.7383C870.293 19.3815 869.311 19.7031 868.059 19.7031H846.73ZM892.494 45.7031C891.242 45.7031 890.26 45.3815 889.549 44.7383C888.838 44.0612 888.482 43.2656 888.482 42.3516C888.482 41.4375 888.838 40.6589 889.549 40.0156C890.26 39.3385 891.242 39 892.494 39H900.822C902.075 39 903.057 39.3385 903.768 40.0156C904.479 40.6589 904.834 41.4375 904.834 42.3516C904.834 43.2656 904.479 44.0612 903.768 44.7383C903.057 45.3815 902.075 45.7031 900.822 45.7031H892.494ZM918.494 45.7031C917.242 45.7031 916.26 45.3815 915.549 44.7383C914.838 44.0612 914.482 43.2656 914.482 42.3516C914.482 41.4375 914.838 40.6589 915.549 40.0156C916.26 39.3385 917.242 39 918.494 39H926.822C928.075 39 929.057 39.3385 929.768 40.0156C930.479 40.6589 930.834 41.4375 930.834 42.3516C930.834 43.2656 930.479 44.0612 929.768 44.7383C929.057 45.3815 928.075 45.7031 926.822 45.7031H918.494ZM892.494 39.2031C891.242 39.2031 890.26 38.8815 889.549 38.2383C888.838 37.5612 888.482 36.7656 888.482 35.8516C888.482 34.9375 888.838 34.1589 889.549 33.5156C890.26 32.8385 891.242 32.5 892.494 32.5H900.822C902.075 32.5 903.057 32.8385 903.768 33.5156C904.479 34.1589 904.834 34.9375 904.834 35.8516C904.834 36.7656 904.479 37.5612 903.768 38.2383C903.057 38.8815 902.075 39.2031 900.822 39.2031H892.494ZM918.494 39.2031C917.242 39.2031 916.26 38.8815 915.549 38.2383C914.838 37.5612 914.482 36.7656 914.482 35.8516C914.482 34.9375 914.838 34.1589 915.549 33.5156C916.26 32.8385 917.242 32.5 918.494 32.5H926.822C928.075 32.5 929.057 32.8385 929.768 33.5156C930.479 34.1589 930.834 34.9375 930.834 35.8516C930.834 36.7656 930.479 37.5612 929.768 38.2383C929.057 38.8815 928.075 39.2031 926.822 39.2031H918.494ZM892.494 32.7031C891.242 32.7031 890.26 32.3815 889.549 31.7383C888.838 31.0612 888.482 30.2656 888.482 29.3516C888.482 28.4375 888.838 27.6589 889.549 27.0156C890.26 26.3385 891.242 26 892.494 26H900.822C902.075 26 903.057 26.3385 903.768 27.0156C904.479 27.6589 904.834 28.4375 904.834 29.3516C904.834 30.2656 904.479 31.0612 903.768 31.7383C903.057 32.3815 902.075 32.7031 900.822 32.7031H892.494ZM918.494 32.7031C917.242 32.7031 916.26 32.3815 915.549 31.7383C914.838 31.0612 914.482 30.2656 914.482 29.3516C914.482 28.4375 914.838 27.6589 915.549 27.0156C916.26 26.3385 917.242 26 918.494 26H926.822C928.075 26 929.057 26.3385 929.768 27.0156C930.479 27.6589 930.834 28.4375 930.834 29.3516C930.834 30.2656 930.479 31.0612 929.768 31.7383C929.057 32.3815 928.075 32.7031 926.822 32.7031H918.494ZM892.494 26.2031C891.242 26.2031 890.26 25.8815 889.549 25.2383C888.838 24.5612 888.482 23.7656 888.482 22.8516C888.482 21.9375 888.838 21.1589 889.549 20.5156C890.26 19.8385 891.242 19.5 892.494 19.5H900.822C902.075 19.5 903.057 19.8385 903.768 20.5156C904.479 21.1589 904.834 21.9375 904.834 22.8516C904.834 23.7656 904.479 24.5612 903.768 25.2383C903.057 25.8815 902.075 26.2031 900.822 26.2031H892.494ZM918.494 26.2031C917.242 26.2031 916.26 25.8815 915.549 25.2383C914.838 24.5612 914.482 23.7656 914.482 22.8516C914.482 21.9375 914.838 21.1589 915.549 20.5156C916.26 19.8385 917.242 19.5 918.494 19.5H926.822C928.075 19.5 929.057 19.8385 929.768 20.5156C930.479 21.1589 930.834 21.9375 930.834 22.8516C930.834 23.7656 930.479 24.5612 929.768 25.2383C929.057 25.8815 928.075 26.2031 926.822 26.2031H918.494ZM892.494 19.7031C891.242 19.7031 890.26 19.3815 889.549 18.7383C888.838 18.0612 888.482 17.2656 888.482 16.3516C888.482 15.4375 888.838 14.6589 889.549 14.0156C890.26 13.3385 891.242 13 892.494 13H920.322C921.575 13 922.557 13.3385 923.268 14.0156C923.979 14.6589 924.334 15.4375 924.334 16.3516C924.334 17.2656 923.979 18.0612 923.268 18.7383C922.557 19.3815 921.575 19.7031 920.322 19.7031H892.494ZM944.758 52.2031C943.505 52.2031 942.523 51.8646 941.812 51.1875C941.102 50.5443 940.746 49.7656 940.746 48.8516C940.746 47.9375 941.102 47.1419 941.812 46.4648C942.523 45.8216 943.505 45.5 944.758 45.5H972.586C973.839 45.5 974.82 45.8216 975.531 46.4648C976.242 47.1419 976.598 47.9375 976.598 48.8516C976.598 49.7656 976.242 50.5443 975.531 51.1875C974.82 51.8646 973.839 52.2031 972.586 52.2031H944.758ZM970.758 45.7031C969.505 45.7031 968.523 45.3815 967.812 44.7383C967.102 44.0612 966.746 43.2656 966.746 42.3516C966.746 41.4375 967.102 40.6589 967.812 40.0156C968.523 39.3385 969.505 39 970.758 39H979.086C980.339 39 981.32 39.3385 982.031 40.0156C982.742 40.6589 983.098 41.4375 983.098 42.3516C983.098 43.2656 982.742 44.0612 982.031 44.7383C981.32 45.3815 980.339 45.7031 979.086 45.7031H970.758ZM951.258 39.2031C950.005 39.2031 949.023 38.8815 948.312 38.2383C947.602 37.5612 947.246 36.7656 947.246 35.8516C947.246 34.9375 947.602 34.1589 948.312 33.5156C949.023 32.8385 950.005 32.5 951.258 32.5H979.086C980.339 32.5 981.32 32.8385 982.031 33.5156C982.742 34.1589 983.098 34.9375 983.098 35.8516C983.098 36.7656 982.742 37.5612 982.031 38.2383C981.32 38.8815 980.339 39.2031 979.086 39.2031H951.258ZM944.758 32.7031C943.505 32.7031 942.523 32.3815 941.812 31.7383C941.102 31.0612 940.746 30.2656 940.746 29.3516C940.746 28.4375 941.102 27.6589 941.812 27.0156C942.523 26.3385 943.505 26 944.758 26H953.086C954.339 26 955.32 26.3385 956.031 27.0156C956.742 27.6589 957.098 28.4375 957.098 29.3516C957.098 30.2656 956.742 31.0612 956.031 31.7383C955.32 32.3815 954.339 32.7031 953.086 32.7031H944.758ZM970.758 32.7031C969.505 32.7031 968.523 32.3815 967.812 31.7383C967.102 31.0612 966.746 30.2656 966.746 29.3516C966.746 28.4375 967.102 27.6589 967.812 27.0156C968.523 26.3385 969.505 26 970.758 26H979.086C980.339 26 981.32 26.3385 982.031 27.0156C982.742 27.6589 983.098 28.4375 983.098 29.3516C983.098 30.2656 982.742 31.0612 982.031 31.7383C981.32 32.3815 980.339 32.7031 979.086 32.7031H970.758ZM944.758 26.2031C943.505 26.2031 942.523 25.8815 941.812 25.2383C941.102 24.5612 940.746 23.7656 940.746 22.8516C940.746 21.9375 941.102 21.1589 941.812 20.5156C942.523 19.8385 943.505 19.5 944.758 19.5H953.086C954.339 19.5 955.32 19.8385 956.031 20.5156C956.742 21.1589 957.098 21.9375 957.098 22.8516C957.098 23.7656 956.742 24.5612 956.031 25.2383C955.32 25.8815 954.339 26.2031 953.086 26.2031H944.758ZM970.758 26.2031C969.505 26.2031 968.523 25.8815 967.812 25.2383C967.102 24.5612 966.746 23.7656 966.746 22.8516C966.746 21.9375 967.102 21.1589 967.812 20.5156C968.523 19.8385 969.505 19.5 970.758 19.5H979.086C980.339 19.5 981.32 19.8385 982.031 20.5156C982.742 21.1589 983.098 21.9375 983.098 22.8516C983.098 23.7656 982.742 24.5612 982.031 25.2383C981.32 25.8815 980.339 26.2031 979.086 26.2031H970.758ZM951.258 19.7031C950.005 19.7031 949.023 19.3815 948.312 18.7383C947.602 18.0612 947.246 17.2656 947.246 16.3516C947.246 15.4375 947.602 14.6589 948.312 14.0156C949.023 13.3385 950.005 13 951.258 13H979.086C980.339 13 981.32 13.3385 982.031 14.0156C982.742 14.6589 983.098 15.4375 983.098 16.3516C983.098 17.2656 982.742 18.0612 982.031 18.7383C981.32 19.3815 980.339 19.7031 979.086 19.7031H951.258Z"
                fill="#DAB3FE"
              />
            </svg>

            <img
              src={logoSeason3}
              alt="XenoRush Season 3"
              className={styles.completionLogo}
            />

            <div className={styles.contentFrame + " !mt-[13rem]"}>
              <div
                className={
                  styles.prizeSection +
                  " !bg-transparent !border-none !p-0 !shadow-none"
                }
              >
                <img
                  src={section2CharactersTop}
                  alt="XenoRush characters"
                  className={
                    styles.prizeStrip +
                    " !w-full !bottom-[calc(100%-1.5rem)] lg:!bottom-[calc(100%-3rem)]"
                  }
                />
                {Date.now() >= ANNOUNCEMENT_DATE.getTime() ? (
                  <img src="/images/winner_table.png" className="w-full " />
                ) : (
                  <div className="py-20 flex flex-col items-center">
                    <p className="text-purple-200/70 mb-8 italic">
                      Pengumuman pemenang akan segera diumumkan. Nantikan! 
                    </p>
                    <Countdown targetDate={ANNOUNCEMENT_DATE} />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      ) : variant === "thank-you" || variant === "registered" ? (
        <div className={`${styles.completionCard} ${styles.thankYouCard}`}>
          <div className={styles.thankYouTextBlock}>
            <p>Pengumuman pemenang akan dilakukan pada:</p>
            <strong>25 April 2026</strong>
            <p>
              Bagi pemenang, periode untuk pengumpulan wallet address akan
              dilaksanakan pada :
            </p>
            <strong>25 April 1 Mei 2026</strong>
            <p>
              Melalui email atau whatsapp <strong>RESMI dari Xenorize</strong>
            </p>
            <p>Hati-hati penipu dan pihak yang mengatasnamakan Xenorize!</p>
            <p>
              Semua informasi akan melalui kanal resmi Xenorize yaitu website
              resmi kami dan juga kanal media sosial resmi kami!
            </p>
          </div>

          <div className={styles.completionInnerCard}>
            <p>Periode distribusi hadiah :</p>
            <strong>+/- 7 hari kerja</strong>
            <strong>Paling lambat pada 12 Mei 2026</strong>
          </div>

          <a href={XENORIZE_URL} className={styles.completionButton}>
            {buttonLabel}
          </a>
        </div>
      ) : (
        <div className={styles.completionCard}>
          <div className={styles.completionInnerCard}>
            <p>{subtitle}</p>
          </div>
          <a href={XENORIZE_URL} className={styles.completionButton}>
            {buttonLabel}
          </a>
        </div>
      )}

      {/* {variant === "winners" && onToggleDefault && (
        <button className={styles.floatingToggle} onClick={onToggleDefault}>
          Lihat Form Pendaftaran
        </button>
      )} */}
    </section>

  {variant === "winners" && (
    <>
      {Date.now() >= ANNOUNCEMENT_DATE.getTime() ? (
        <>
          {/* Congrats section */}
          <section
            className={styles.promoSection + " bg-black py-20 max-md:!p-5"}
          >
            <div
              className={`${styles.completionCard} ${styles.thankYouCard} !w-full !max-w-[1172px] mx-auto !p-5 !py-8`}
            >
              <h1 className="text-[32px] text-white">
                Selamat kepada seluruh pemenang!
              </h1>
              <ol className="list-decimal w-full max-w-[798px] mx-auto space-y-4 !p-5 !pb-2">
                <li className="leading-[1.6]">
                  Periksa Email Secara Berkala: Pastikan Anda memeriksa inbox di
                  alamat email yang telah didaftarkan di XenoRush untuk
                  informasi lebih lanjut.
                </li>
                <li className="leading-[1.6]">
                  Isi Data Melalui Formulir yang Dikirim: Jangan lupa untuk
                  mengisi data melalui form yang dikirimkan via WhatsApp atau
                  email. Pastikan data yang dimasukkan lengkap dan akurat.
                </li>
                <li className="leading-[1.6]">
                  Cek ERC-20 Wallet Address Anda: <br /> Pastikan alamat wallet
                  ERC-20 yang Anda masukkan sudah benar. Kesalahan input atau
                  ketidaksesuaian jaringan wallet dapat menyebabkan hadiah tidak
                  dapat dikirim atau tidak bisa diklaim.
                </li>
              </ol>

              <div
                className={`${styles.completionCard} ${styles.thankYouCard} !w-full !max-w-[798px] mx-auto`}
              >
                <div className="!font-light text-center items-center">
                  Timeline: <br />
                  Pengumuman pemenang :{" "}
                  <strong className="!font-bold inline">
                    {" "}
                    25 April 2026
                  </strong>{" "}
                  <br />
                  Pengumpulan data pemenang :{" "}
                  <strong className="!font-bold inline">
                    25 April - 1 Mei 2026
                  </strong>
                  <br />
                  Distribusi hadiah :{" "}
                  <strong className="!font-bold inline">
                    +/- 7 hari kerja mulai dari 2 Mei 2026{" "}
                  </strong>
                </div>
              </div>
            </div>
          </section>

          {/* Promo section */}
          <section className={styles.promoSection + " !py-20"}>
            <div className={styles.contentFrame}>
              <div className={styles.promoCard}>
                <div className={styles.promoText}>
                  <h2 className={styles.sectionHeading}>
                    Simplifying Trade Amplifying Return with Xenorize
                  </h2>
                  <p>
                    Xenorize adalah ekosistem yang membantu kamu trading dengan
                    lebih sederhana, terarah, dan konsisten.
                  </p>
                  <a href="/login" className={styles.promoButton}>
                    Coba 30-Hari Gratis!
                    <img src={arrowRight} alt="" aria-hidden="true" />
                  </a>
                </div>

                <div className={styles.promoVisuals}>
                  <div className={styles.monitorWrap}>
                    <div className={styles.monitorScreenInset}>
                      <img
                        src={monitorScreen}
                        alt="Xenorize dashboard preview"
                        className={styles.dashboardPreview}
                      />
                    </div>
                    <img
                      src={monitorFrameAsset}
                      alt="Monitor Xenorize"
                      className={styles.monitorFrameImage}
                    />
                  </div>

                  <img
                    src={promoGirlPink}
                    alt="Pink Xenorush girl"
                    className={styles.promoCharacterLeft}
                  />
                  <img
                    src={promoBoyLaying}
                    alt="Blue Xenorush boy"
                    className={styles.promoCharacterRight}
                  />
                </div>
              </div>
            </div>
          </section>

          <div className="flex justify-center pb-8">
            <img
              src={logoSeason3}
              alt="XenoRush Season 3"
              className="w-[180px] h-auto opacity-90"
            />
          </div>
        </>
      ) : null}

      {/* Footer always visible */}
      <Footer />
    </>
  )}
  </>
);

export const XenoRush = ({ initialPageMode = "default" }) => {
  const formRef = useRef(null);
  const { authedPost } = useAuthedApi();

  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    password: "",
    retype_password: "",
    whatsapp: "",
    safe_word: "",
  });
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [isVerificationOpen, setVerificationOpen] = useState(false);
  const [verificationToken, setVerificationToken] = useState(null);
  const [pageMode, setPageMode] = useState(initialPageMode);

  const isRegistrationClosed = Date.now() >= REGISTRATION_DEADLINE.getTime();
  const isAnnouncementTime = Date.now() >= ANNOUNCEMENT_DATE.getTime();

  const normalizedWhatsapp = useMemo(
    () => normalizeWhatsapp(formData.whatsapp),
    [formData.whatsapp],
  );

  const handleChange = (event) => {
    const { id, value } = event.target;
    setFormData((prev) => ({
      ...prev,
      [id]: value,
    }));
  };

  const scrollToForm = () => {
    formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const validateForm = () => {
    const {
      first_name,
      last_name,
      email,
      password,
      retype_password,
      safe_word,
    } = formData;

    if (!first_name.trim()) return "Nama depan wajib diisi.";
    if (!last_name.trim()) return "Nama belakang wajib diisi.";
    if (!email.trim()) return "Email wajib diisi.";
    if (!password.trim()) return "Password wajib diisi.";
    if (!retype_password.trim()) return "Konfirmasi password wajib diisi.";
    if (password !== retype_password) return "Password tidak sama.";
    if (!formData.whatsapp.trim()) return "Nomor WhatsApp wajib diisi.";
    if (!isValidWhatsapp(normalizedWhatsapp)) {
      return "Nomor WhatsApp tidak valid. Pakai format Indonesia yang aktif.";
    }
    if (!safe_word.trim()) return "Safe Word wajib diisi.";
    if (safe_word.trim().length < 3) return "Safe Word minimal 3 karakter.";
    if (!termsAccepted) return "Kamu harus menyetujui syarat dan ketentuan.";

    const hasMinLength = password.length >= 8;
    const hasUppercase = /[A-Z]/.test(password);
    const hasLowercase = /[a-z]/.test(password);
    const hasSymbol = /[!@#$%^&*(),.?":{}|<>]/.test(password);

    if (!(hasMinLength && hasUppercase && hasLowercase && hasSymbol)) {
      return "Password harus minimal 8 karakter dan mengandung huruf besar, huruf kecil, serta simbol.";
    }

    return "";
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (loading || isRegistrationClosed) return;

    const validationError = validateForm();
    setError(validationError);
    setSuccessMessage("");

    if (validationError) return;

    setLoading(true);
    try {
      const signupResponse = await authedPost(
        "/api/signup/",
        {
          first_name: formData.first_name.trim(),
          last_name: formData.last_name.trim(),
          email: formData.email.trim(),
          password: formData.password,
          whatsapp: normalizedWhatsapp,
          phising_code: formData.safe_word.trim(),
        },
        {
          params: {
            code: XENORUSH_REFERRAL_CODE,
          },
        },
      );

      localStorage.removeItem("verification_code_signup");
      localStorage.removeItem("verification_code_signin");

      if (signupResponse.data.verification_token) {
        setVerificationToken(signupResponse.data.verification_token);
        setVerificationOpen(true);
      }

      if (signupResponse.data.user_active_immediately) {
        setSuccessMessage(
          "Akun kamu sudah dibuat dan raffle entry kamu sudah masuk.",
        );
        setPageMode("thank-you");
      } else {
        setSuccessMessage(
          "Pendaftaran kamu berhasil. Selesaikan verifikasi email untuk mengaktifkan akunmu.",
        );
      }
    } catch (submitError) {
      const payload = submitError.response?.data;
      const message = extractErrorMessage(
        payload || "Pendaftaran gagal. Coba lagi.",
      );
      const joined = JSON.stringify(payload || {}).toLowerCase();
      const loweredMessage = message.toLowerCase();

      if (
        /whatsapp|phone|mobile|nomor/.test(joined) ||
        /whatsapp|phone|mobile|nomor/.test(loweredMessage)
      ) {
        setError("Nomor WhatsApp sudah digunakan, silakan pakai nomor lain.");
        return;
      }

      if (/email/.test(joined) || /email/.test(loweredMessage)) {
        setError("Email sudah digunakan, silakan login");
        return;
      }

      if (
        /already|exist|terdaftar|registered/.test(loweredMessage) ||
        /already|exist|terdaftar|registered/.test(joined)
      ) {
        setError(
          "Data sudah digunakan. Cek kembali email atau nomor WhatsApp kamu.",
        );
        return;
      }

      setError(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.page}>
      <Helmet>
        <title>XenoRush Season 3 2026 | Xenorize</title>
        <meta
          name="description"
          content="Join XenoRush Season 3 2026. Buat akun Xenorize, masuk raffle otomatis, dan menangkan total hadiah Rp15.000.000 tanpa biaya pendaftaran."
        />
        <link rel="canonical" href="https://xenosandbox.com/xenorush/" />
      </Helmet>

      <DialogVerifyRegister
        isVerificationOpen={isVerificationOpen}
        setVerificationOpen={setVerificationOpen}
        userId={verificationToken}
        email={formData.email}
        onVerified={() => setPageMode("thank-you")}
      />

      {pageMode === "winners" || (pageMode === "default" && isRegistrationClosed) ? (
        <CompletionState
          title=""
          subtitle=""
          buttonLabel="Pelajari Xenorize"
          variant="winners"
          onToggleDefault={() => setPageMode("form")}
        />
      ) : pageMode === "thank-you" ? (
        <CompletionState
          title="Thank You!"
          subtitle=""
          buttonLabel="Pelajari Xenorize"
          variant="thank-you"
        />
      ) : pageMode === "registered" ? (
        <CompletionState
          title="Kamu sudah terdaftar di XenoRush"
          subtitle=""
          buttonLabel="Pelajari Xenorize"
          variant="registered"
        />
      ) : (
        <main className={styles.main}>
          <section className={styles.heroSection}>
            <div className={styles.heroBackdrop} />
            <div className={styles.contentFrame}>
              <div className={styles.heroStageWrap}>
                <img
                  src={section1HeroBackground}
                  alt=""
                  className={styles.heroBackgroundImage}
                  aria-hidden="true"
                />

                <div className={styles.heroDecorLayer} aria-hidden="true">
                  <img
                    src={section1Deco07}
                    alt=""
                    className={`${styles.heroTinyDeco} ${styles.heroTinyDecoOne}`}
                  />
                  <img
                    src={section1Deco08}
                    alt=""
                    className={`${styles.heroTinyDeco} ${styles.heroTinyDecoTwo}`}
                  />
                  <img
                    src={section1Deco04}
                    alt=""
                    className={`${styles.heroTinyDeco} ${styles.heroTinyDecoThree}`}
                  />
                  <img
                    src={section1Deco02}
                    alt=""
                    className={`${styles.heroTinyDeco} ${styles.heroTinyDecoFour}`}
                  />
                  <img
                    src={section1Deco05}
                    alt=""
                    className={`${styles.heroTinyDeco} ${styles.heroTinyDecoFive}`}
                  />
                  <img
                    src={section1Deco06}
                    alt=""
                    className={`${styles.heroTinyDeco} ${styles.heroTinyDecoSix}`}
                  />
                  <img
                    src={section1Deco08}
                    alt=""
                    className={`${styles.heroTinyDeco} ${styles.heroTinyDecoSeven}`}
                  />
                  <img
                    src={section1Deco07}
                    alt=""
                    className={`${styles.heroTinyDeco} ${styles.heroTinyDecoEight}`}
                  />
                  <img
                    src={section1HeroGridAccent}
                    alt=""
                    className={styles.heroCoinTop}
                  />
                </div>

                <div className={styles.heroGridPlane} aria-hidden="true" />

                <div className={styles.heroContent}>
                  <div className={styles.heroTextWrap}>
                    <div className={styles.heroEyebrow}>
                      XenoRush Season 3 2026
                    </div>
                    <h1 className={styles.heroTitle}>
                      Menangkan Total Hadiah
                      <span>
                        <img
                          src={section1HeroAmount}
                          alt="Rp15.000.000"
                          className={styles.heroAmountImage}
                        />
                      </span>
                    </h1>
                    <p className={styles.heroCopy}>
                      Ayo join XenoRush! Campaign terbatas dari Xenorize di mana
                      kamu cukup membuat akun untuk kesempatan menang giveaway
                    </p>
                    <button
                      className={styles.heroButton}
                      onClick={scrollToForm}
                    >
                      Join Sekarang!
                    </button>
                  </div>
                </div>

                <img
                  src={section1HeroLeft}
                  alt="Yellow Xenorush girl"
                  className={`${styles.heroCharacterLeft} ${styles.heroCharacterDesktop}`}
                />
                <img
                  src={section1HeroRight}
                  alt="Purple Xenorush boy"
                  className={`${styles.heroCharacterRight} ${styles.heroCharacterDesktop}`}
                />
                <img
                  src={section1HeroLeftMobile}
                  alt="Yellow Xenorush girl"
                  className={`${styles.heroCharacterLeft} ${styles.heroCharacterMobile}`}
                />
                <img
                  src={section1HeroRightMobile}
                  alt="Purple Xenorush boy"
                  className={`${styles.heroCharacterRight} ${styles.heroCharacterMobile}`}
                />
              </div>
            </div>
          </section>

          <section
            className={styles.prizeSectionShell}
            style={{
              backgroundImage: `linear-gradient(rgba(8, 6, 20, 0.1), rgba(8, 6, 20, 0.18)), url(${section2Background})`,
            }}
          >
            <div className={styles.contentFrame}>
              <div className={styles.prizeSection}>
                <img
                  src={section2CharactersTop}
                  alt="XenoRush characters"
                  className={styles.prizeStrip}
                />
                <div className={styles.prizeCard}>
                  <div className={styles.prizeMainBlock}>
                    <div className={styles.prizeGrandCard}>
                      <div className={styles.prizeLabel}>Grand Prize</div>
                      <img
                        src={section2PrizeRp5m}
                        alt="Rp5.000.000"
                        className={styles.prizeBadge}
                      />
                      <p className={styles.prizeMeta}>1 pemenang utama</p>
                    </div>
                    <div className={styles.prizeMiniGrid}>
                      <div>
                        <img
                          src={section2TokenGold}
                          alt=""
                          className={styles.prizeTokenImage}
                        />
                        <strong>Rp1.000.000,-</strong>
                        <span>5 Pemenang</span>
                      </div>
                      <div>
                        <img
                          src={section2TokenSilver}
                          alt=""
                          className={styles.prizeTokenImage}
                        />
                        <strong>Rp50.000,-</strong>
                        <span>100 Pemenang</span>
                      </div>
                    </div>
                  </div>
                  <p className={styles.prizeFootnote}>
                    <span className={styles.prizeFootnoteUsd}>
                      Hadiah dalam USDT
                    </span>
                    <span>
                      Semua proses pendaftaran dan pembagian hadiah tidak
                      dipungut biaya
                    </span>
                  </p>
                </div>
              </div>
            </div>
          </section>

          <section className={styles.infoSection}>
            <div className={styles.contentFrame}>
              <div className={styles.infoGrid}>
                <div className={styles.leftColumn}>
                  <div className={styles.glassCard}>
                    <h2 className={styles.sectionHeading}>Cara Bergabung</h2>
                    <p className={styles.sectionLead}>
                      Cara ikut XenoRush Season 3
                    </p>
                    <div className={styles.stepsList}>
                      <div className={styles.stepItem}>
                        <span>1</span>
                        <p>
                          Buat akun Xenorize dan isi data lengkap dengan benar.
                        </p>
                      </div>
                      <div className={styles.stepItem}>
                        <span>2</span>
                        <p>
                          Setelah signup berhasil, akun kamu langsung masuk ke
                          raffle XenoRush.
                        </p>
                      </div>
                      <div className={styles.stepItem}>
                        <span>3</span>
                        <p>
                          Pastikan Anda mengikuti kanal resmi dan{" "}
                          <a
                            href="https://www.instagram.com/xenorize.global/"
                            target="_blank"
                            rel="noreferrer"
                          >
                            Instagram resmi Xenorize
                          </a>{" "}
                          untuk mendapatkan informasi terbaru dan pengumuman
                          pemenang.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className={`${styles.glassCard} ${styles.timelineCard}`}>
                    <h3 className={styles.timelineHeading}>Timeline</h3>
                    <div className={styles.timelineRows}>
                      <p>
                        <span>Periode XenoRush:</span> 18 – 20 April 2026
                      </p>
                      <p>
                        <strong>Ditutup: 20 April 2026, 23:59 (GMT+7)</strong>
                      </p>
                      <p>
                        <span>Pengumuman pemenang:</span>{" "}
                        <strong>{ANNOUNCEMENT_DATE_LABEL}</strong>
                      </p>
                      <p>
                        <strong>Melalui email yang sudah terdaftar</strong>
                      </p>
                    </div>
                    <p className={styles.timelineFootnote}>
                      Hati-hati terhadap pihak yang mengatasnamakan Xenorize.
                      Seluruh proses XenoRush tidak dipungut biaya, dan
                      informasi resmi hanya disampaikan melalui kanal resmi
                      Xenorize.
                    </p>
                  </div>
                </div>

                <div ref={formRef} className={styles.formCard}>
                  <div className={styles.formHeader}>
                    <h3>Daftar Sekarang</h3>

                    <p>
                      {isRegistrationClosed ? (
                        <>
                          Pendaftaran XenoRush telah ditutup. Terima kasih atas
                          antusiasmenya.
                        </>
                      ) : (
                        <>
                          Isi semua data dengan benar untuk ikut giveaway
                          XenoRush.
                        </>
                      )}
                    </p>
                  </div>

                  {error && <p className={styles.errorMessage}>{error}</p>}

                  <form onSubmit={handleSubmit} className={styles.form}>
                    <div className={styles.formGridTwo}>
                      <div className={styles.formGroup}>
                        <label htmlFor="first_name">Nama Depan</label>
                        <input
                          id="first_name"
                          type="text"
                          placeholder="Masukkan nama depan"
                          value={formData.first_name}
                          onChange={handleChange}
                          disabled={isRegistrationClosed}
                          required
                        />
                      </div>
                      <div className={styles.formGroup}>
                        <label htmlFor="last_name">Nama Belakang</label>
                        <input
                          id="last_name"
                          type="text"
                          placeholder="Masukkan nama belakang"
                          value={formData.last_name}
                          onChange={handleChange}
                          disabled={isRegistrationClosed}
                          required
                        />
                      </div>
                    </div>

                    <div className={styles.formGroup}>
                      <label htmlFor="email">Email</label>
                      <input
                        id="email"
                        type="email"
                        placeholder="Masukkan email"
                        value={formData.email}
                        onChange={handleChange}
                        disabled={isRegistrationClosed}
                        required
                      />
                    </div>

                    <div className={styles.formGroup}>
                      <label htmlFor="password">Password</label>
                      <div className={styles.passwordWrapper}>
                        <input
                          id="password"
                          type={showPassword ? "text" : "password"}
                          placeholder="Masukkan password"
                          value={formData.password}
                          onChange={handleChange}
                          disabled={isRegistrationClosed}
                          required
                        />
                        <button
                          type="button"
                          className={styles.eyeButton}
                          onClick={() => setShowPassword((prev) => !prev)}
                          aria-label={
                            showPassword ? "Hide password" : "Show password"
                          }
                        >
                          <PasswordToggleIcon hidden={showPassword} />
                        </button>
                      </div>
                      {!!formData.password.length && (
                        <div className={styles.passwordHint}>
                          <PasswordStrengthIndicator
                            password={formData.password}
                          />
                        </div>
                      )}
                    </div>

                    <div className={styles.formGroup}>
                      <label htmlFor="retype_password">
                        Konfirmasi Password
                      </label>
                      <div className={styles.passwordWrapper}>
                        <input
                          id="retype_password"
                          type={showConfirmPassword ? "text" : "password"}
                          placeholder="Ulangi password"
                          value={formData.retype_password}
                          onChange={handleChange}
                          disabled={isRegistrationClosed}
                          required
                        />
                        <button
                          type="button"
                          className={styles.eyeButton}
                          onClick={() =>
                            setShowConfirmPassword((prev) => !prev)
                          }
                          aria-label={
                            showConfirmPassword
                              ? "Hide password confirmation"
                              : "Show password confirmation"
                          }
                        >
                          <PasswordToggleIcon hidden={showConfirmPassword} />
                        </button>
                      </div>
                    </div>

                    <div className={styles.formGroup}>
                      <label htmlFor="whatsapp">Nomor WhatsApp</label>
                      <div className={styles.whatsappRow}>
                        <div className={styles.flagBadge}>+62</div>
                        <input
                          id="whatsapp"
                          type="tel"
                          placeholder="81234567890"
                          value={formData.whatsapp}
                          onChange={handleChange}
                          disabled={isRegistrationClosed}
                          required
                        />
                      </div>
                    </div>

                    <div className={styles.formGroup}>
                      <label htmlFor="safe_word">Kode Anti Phishing</label>
                      <input
                        id="safe_word"
                        type="text"
                        placeholder="Masukkan safe word"
                        value={formData.safe_word}
                        onChange={handleChange}
                        disabled={isRegistrationClosed}
                        required
                      />
                    </div>

                    <label className={styles.checkboxRow}>
                      <input
                        type="checkbox"
                        checked={termsAccepted}
                        disabled={isRegistrationClosed}
                        onChange={(event) =>
                          setTermsAccepted(event.target.checked)
                        }
                      />
                      <span
                        className={styles.checkboxCustom}
                        aria-hidden="true"
                      />
                      <span className={styles.checkboxLabel}>
                        <a href="#terms-section">Syarat dan Ketentuan</a>
                      </span>
                    </label>

                    {successMessage && (
                      <p className={styles.successMessage}>{successMessage}</p>
                    )}

                    <button
                      type="submit"
                      className={styles.submitButton}
                      disabled={isRegistrationClosed}
                    >
                      {loading ? (
                        <span className="animate-spin w-fit flex justify-center items-center mx-auto">
                          <IconLoading className={styles.loadingIcon} />
                        </span>
                      ) : (
                        "Buat Akun"
                      )}
                    </button>
                  </form>
                </div>
              </div>
            </div>
          </section>

          <section className={styles.faqSection}>
            <div className={styles.contentFrame}>
              <div className={styles.faqGrid}>
                <div className={styles.faqHeadingWrap}>
                  <h2 className={styles.faqHeading}>
                    Frequently Asked Questions (FAQ)
                  </h2>
                </div>
                <div className={styles.faqList}>
                  {faqItems.map((item) => (
                    <details key={item.question} className={styles.faqItem}>
                      <summary>{item.question}</summary>
                      <p>{item.answer}</p>
                    </details>
                  ))}
                </div>
              </div>
            </div>
          </section>

          <section className={styles.promoSection}>
            <div className={styles.contentFrame}>
              <div className={styles.promoCard}>
                <div className={styles.promoText}>
                  <h2 className={styles.sectionHeading}>
                    Simplifying Trade Amplifying Return with Xenorize
                  </h2>
                  <p>
                    Xenorize adalah ekosistem yang membantu kamu trading dengan
                    lebih sederhana, terarah, dan konsisten.
                  </p>
                  <a href="/login" className={styles.promoButton}>
                    Coba 30-Hari Gratis!
                    <img src={arrowRight} alt="" aria-hidden="true" />
                  </a>
                </div>

                <div className={styles.promoVisuals}>
                  <div className={styles.monitorWrap}>
                    <div className={styles.monitorScreenInset}>
                      <img
                        src={monitorScreen}
                        alt="Xenorize dashboard preview"
                        className={styles.dashboardPreview}
                      />
                    </div>
                    <img
                      src={monitorFrameAsset}
                      alt="Monitor Xenorize"
                      className={styles.monitorFrameImage}
                    />
                  </div>

                  <img
                    src={promoGirlPink}
                    alt="Pink Xenorush girl"
                    className={styles.promoCharacterLeft}
                  />
                  <img
                    src={promoBoyLaying}
                    alt="Blue Xenorush boy"
                    className={styles.promoCharacterRight}
                  />
                </div>
              </div>
            </div>
          </section>

          <section id="terms-section" className={styles.termsSection}>
            <div className={styles.contentFrame}>
              <div className={styles.termsCard}>
                <h3>Syarat dan Ketentuan</h3>
                <ul>
                  {termsItems.map((item) => (
                    <li key={item}>
                      <span
                        className={styles.termsCheckIcon}
                        aria-hidden="true"
                      >
                        ✓
                      </span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </section>

          <div className="flex justify-center pb-8">
            <img
              src={logoSeason3}
              alt="XenoRush Season 3"
              className="w-[180px] h-auto opacity-90"
            />
          </div>
          <Footer />
        </main>
      )}
    </div>
  );
};
